/**
 * Patrick utility object
 * @namespace Patrick
 */
window.Patrick = {
  /**
   * Emits a custom event on the specified element
   * @param {HTMLElement} element - The element to dispatch the event on
   * @param {string} name - The name of the custom event
   * @param {*} data - The data to pass as the event detail
   * @returns {void}
   */
  emitEvent(element, name, data) {
    element.dispatchEvent(new CustomEvent(name, { detail: data }));
  },
};

{
  /**
   * Custom element for managing a group of tabs
   * @class TabGroup
   * @extends HTMLElement
   */
  class TabGroup extends HTMLElement {
    /**
     * Creates a TabGroup instance
     * @constructor
     */
    constructor() {
      super();
      const tabs = this.querySelectorAll('patrick-tab');
      tabs.forEach((tab) => {
        const targetTab = tab;
        // Whenever a tab gets open close the other tabs
        targetTab.addEventListener('patrick:tab/show', () => {
          for (const tab of tabs) {
            if (tab != targetTab) tab.hide();
          }
        });
      });
    }
  }

  /**
   * Custom element for individual tabs with show/hide functionality
   * @class Tab
   * @extends HTMLElement
   */
  class Tab extends HTMLElement {
    /**
     * CSS class name applied when tab is open
     * @static
     * @type {string}
     */
    static openClass = 'open';

    /**
     * Array of elements that can toggle this tab
     * @type {HTMLElement[]}
     */
    togglers = [];

    /**
     * Creates a Tab instance
     * @constructor
     */
    constructor() {
      super();
      this.show = this.show.bind(this);
      this.hide = this.hide.bind(this);

      const target = this.id;
      // Init the togglers
      document.querySelectorAll(`[aria-controls="${target}"]`).forEach((toggler) => {
        this.togglers.push(toggler);
        // Hovering (or focusing) a toggler opens its panel, so a linked tab keeps its click for navigation
        const showOnHover = () => {
          // The already-open tab is left alone, otherwise re-hovering it replays the panel animation
          if (this.classList.contains(Tab.openClass)) return;
          this.show();
        };

        toggler.addEventListener('mouseenter', showOnHover);
        toggler.addEventListener('focus', showOnHover);
        toggler.addEventListener('click', (event) => {
          const href = toggler.getAttribute('href');
          if (!href || href === '#') event.preventDefault();
          showOnHover();
        });
      });
    }

    /**
     * Shows the tab by adding the open class and updating ARIA attributes
     * @returns {void}
     */
    show() {
      this.togglers.forEach((toggler) => toggler.setAttribute('aria-expanded', 'true'));
      this.setAttribute('aria-hidden', 'false');
      this.classList.add(Tab.openClass);
      window.Patrick.emitEvent(this, 'patrick:tab/show');
    }

    /**
     * Hides the tab by removing the open class and updating ARIA attributes
     * @returns {void}
     */
    hide() {
      this.togglers.forEach((toggler) => toggler.setAttribute('aria-expanded', 'false'));
      this.setAttribute('aria-hidden', 'true');
      this.classList.remove(Tab.openClass);
      window.Patrick.emitEvent(this, 'patrick:tab/hide');
    }
  }

  // Register custom elements if not already defined
  if (!window.customElements.get('patrick-tab-group')) {
    window.customElements.define('patrick-tab-group', TabGroup);
  }

  if (!window.customElements.get('patrick-tab')) {
    window.customElements.define('patrick-tab', Tab);
  }

  /**
   * Initialize header menu behavior on DOM content loaded
   */
  document.addEventListener('DOMContentLoaded', () => {
    // Toggle class for header when mega menus open or close
    /** @type {HTMLElement|null} */
    const header = document.querySelector('.shopify-section--header');
    if (!header) throw new Error('Header not found');

    document.querySelectorAll('header-mega-menu').forEach((menu) => {
      // Toggle 'menu-open' class on header when a mega menu opens or closes
      menu.addEventListener('dialog:before-show', () => header.classList.add('menu-open'));
      menu.addEventListener('dialog:before-hide', () => header.classList.remove('menu-open'));
      // Close button
      menu.querySelector(`[aria-controls=${menu.id}]`)?.addEventListener('click', menu.hide);
    });

    // theme.js only closes on pointerenter of a non-ancestor, so the header's own blank areas never close it; the panel is a header descendant, so pointerleave fires only once the pointer is outside both bar and panel.
    if (window.matchMedia('screen and (pointer: fine)').matches) {
      header.addEventListener('pointerleave', () => {
        header
          .querySelectorAll('header-mega-menu[open], header-dropdown-menu[open]')
          .forEach((menu) => menu.hide());
      });
    }
  });
}
/**product card animation */
import { animate as animate7, stagger } from 'vendor';

function animatePanel(panel, direction = 'rightToLeft', forceAll = false) {  
  const selector = forceAll
    ? 'product-card, .intro-block img, .intro-block video, .homepage_blog .blog-post-card--horizontal .blog-post-card__image-container' // ✅ animate product-cards + intro-block media
    : "product-card[reveal-on-scroll='true'], .product-list__promo[reveal-on-scroll='true'], .intro-block img, .intro-block video, .homepage_blog .blog-post-card--horizontal .blog-post-card__image-container";

  const items = panel.querySelectorAll(selector);
  if (!items.length) return;

  items.forEach((el) => {
    el.style.opacity = '0';
    if (direction === 'bottomToTop') {
      el.style.clipPath = 'inset(100% 0 0 0)';
    } else if (direction === 'rightToLeft') {
      el.style.clipPath = 'inset(0 0 0 100%)';
    } else if (direction === 'leftToRight') {
      el.style.clipPath = 'inset(0 100% 0 0)';
    }
  });

  requestAnimationFrame(() => {
    if (getComputedStyle(panel).display === 'none' || panel.offsetParent === null) {
      setTimeout(() => animatePanel(panel, direction, forceAll), 50);
      return;
    }

    const animationConfig = {
      duration: 1.2,
      ease: 'easeInOut',
      delay: stagger(0.15),
      startDelay: 0.5,
    };

    if (direction === 'bottomToTop') {
      animate7(
        items,
        {
          opacity: [0, 1],
          clipPath: ['inset(100% 0 0 0)', 'inset(0 0 0 0)'],
        },
        animationConfig,
      );
    } else if (direction === 'rightToLeft') {
      animate7(
        items,
        {
          opacity: [0, 1],
          clipPath: ['inset(0 0 0 100%)', 'inset(0 0 0 0)'],
        },
        animationConfig,
      );
    } else if (direction === 'leftToRight') {
      animate7(
        items,
        {
          opacity: [0, 1],
          clipPath: ['inset(0 100% 0 0)', 'inset(0 0 0 0)'],
        },
        animationConfig,
      );
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  // ── Featured collections: tab-change media reveal ───────────────────────────
  // Scoped per section on purpose: <featured-collections-tabs> is also used by other
  // sections (.shopify-section--shades), and a page can hold more than one
  // featured-collections section, so a single document-level lookup binds the wrong
  // tab list — and that section's images then never change at all.
  const fcSections = Array.from(document.querySelectorAll('.shopify-section--featured-collections')).filter(
    (section) => section.querySelector('featured-collections-tabs') && section.querySelector('.intro-block'),
  );

  fcSections.forEach((section) => {
    const tabList = section.querySelector('featured-collections-tabs');
    const tabArr = Array.from(tabList.querySelectorAll("[role='tab']"));
    const cells = Array.from(section.querySelectorAll('.featured-collections__media'));
    const introBlock = section.querySelector('.intro-block');
    if (!tabArr.length || !cells.length) return;

    const REVEAL_DURATION = 700;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const innerOf = (cell) => (cell ? cell.querySelector('img, video') : null);

    // One source of truth for the timing: the outgoing media's blur + overlay ramp
    // (CSS transitions on .fc-media-under) reads this, so it can never drift from
    // the wipe and always lands exactly when the incoming media has fully covered it.
    section.style.setProperty('--fc-reveal-duration', `${REVEAL_DURATION}ms`);

    // The inactive medias are `hidden` + loading="lazy", so nothing is fetched until
    // reveal time and that fetch/decode then lands inside the first frames of the wipe.
    const preloadFcMedia = () => {
      cells.forEach((cell) => {
        const media = innerOf(cell);
        if (!media || media.tagName !== 'IMG') return;
        if (media.loading === 'lazy') media.loading = 'eager';
        if (media.decode) media.decode().catch(() => {});
      });
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(preloadFcMedia, { timeout: 2000 });
    } else {
      setTimeout(preloadFcMedia, 1200);
    }

    // Scroll reveal of the initially-active panel.
    const initialTab = tabArr.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabArr[0];
    const initialPanel = document.getElementById(initialTab.getAttribute('aria-controls'));
    if (initialPanel) {
      const panelObserver = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              animatePanel(initialPanel, 'bottomToTop');
              obs.unobserve(entry.target);
            }
          });
        },
        // rootMargin fires this while the section is still ~12% of a viewport
        // BELOW the fold, so animatePanel's `opacity: 0` set-up happens
        // off-screen. At threshold 0.2 with no margin it fired only once the
        // section was already 20% visible and painted, so the viewer saw the
        // image normally, watched it vanish, then watched it fade back in.
        // Same code path as before - only the trigger moves earlier; if the
        // observer somehow fires late (deep link, restored scroll), behaviour
        // degrades to exactly what it was, never to a hidden image.
        { threshold: 0, rootMargin: '0px 0px 12% 0px' },
      );
      panelObserver.observe(tabList);
    }

    // ── Reveal state ──────────────────────────────────────────────────────────
    // `activeId` is the single source of truth for which media is showing and
    // `pendingId` the one a running wipe is bringing in. Deriving the outgoing media
    // from "whichever cell is not .hidden" is what made fast tab switching glitch:
    // an interrupted wipe leaves two cells visible, and the next change then picked
    // the wrong one — or none — as the outgoing image.
    let activeId = (cells.find((cell) => !cell.classList.contains('hidden')) || cells[0]).dataset.id;
    let pendingId = null;
    let seqCounter = 0;

    // Drop every animation artefact: reveal classes, inline guard values, running wipe.
    const resetCells = () => {
      cells.forEach((cell) => {
        cell.classList.remove('fc-media-reveal', 'fc-media-under');
        cell.style.transform = '';
        const inner = innerOf(cell);
        if (inner) {
          inner.style.transform = '';
          inner.style.opacity = '';
          inner.style.filter = '';
        }
        if (cell.fcWipe) {
          cell.fcWipe.forEach((anim) => anim.cancel());
          cell.fcWipe = null;
        }
      });
    };

    // Land on `id` with no animation: exactly one visible cell, no leftovers.
    const commit = (id) => {
      activeId = id;
      pendingId = null;
      resetCells();
      cells.forEach((cell) => cell.classList.toggle('hidden', cell.dataset.id !== id));
    };

    const reveal = (panelId, goingNext) => {
      const nextCell = cells.find((cell) => cell.dataset.id === panelId);
      if (!nextCell) return;

      if (pendingId === panelId) return; // its wipe is already running
      // Interrupting a wipe: the media it was bringing in becomes the outgoing one
      // (latest wins), so the reveal never jumps back to an older image.
      if (pendingId) activeId = pendingId;
      pendingId = null;

      if (panelId === activeId) return commit(panelId); // already showing — normalise only

      // No wipe when it cannot be seen or is not wanted: below 1000px .intro-block is
      // display:none, and reduced-motion users get a plain swap.
      if (reducedMotion.matches || getComputedStyle(introBlock).display === 'none') {
        commit(panelId);
        return;
      }

      const prevCell = cells.find((cell) => cell.dataset.id === activeId);
      const nextImg = innerOf(nextCell);
      const seq = ++seqCounter;
      pendingId = panelId;

      // Exactly two cells take part: the outgoing one underneath and the incoming one
      // on top. Everything else is hidden immediately.
      resetCells();
      cells.forEach((cell) => cell.classList.toggle('hidden', cell !== nextCell && cell !== prevCell));

      nextCell.classList.add('fc-media-reveal');

      // Next tab = mask opens from the left edge, prev tab = from the right.
      const fromOffset = goingNext ? -100 : 100;

      // Pin the window off-stage BEFORE the first paint, so the media is never flashed
      // unmasked while we wait for decode.
      nextCell.style.transform = `translate3d(${fromOffset}%, 0, 0)`;
      if (nextImg) nextImg.style.transform = `translate3d(${-fromOffset}%, 0, 0)`;

      const startWipe = () => {
        if (seq !== seqCounter) return; // a newer tab change took over

        // One shared timing for both halves — a single frame of drift between the
        // window and the media inside it shows up as the media shifting.
        const timing = { duration: REVEAL_DURATION, easing: 'ease-in-out', fill: 'none' };
        const anims = [
          nextCell.animate(
            [{ transform: `translate3d(${fromOffset}%, 0, 0)` }, { transform: 'translate3d(0, 0, 0)' }],
            timing,
          ),
        ];

        // Counter-slide only: the incoming media is never blurred or dimmed. The blur
        // and the 20% black overlay belong to the outgoing media (.fc-media-under, in
        // CSS) so they read as "being covered" rather than as a fade-in.
        if (nextImg) {
          anims.push(
            nextImg.animate(
              [{ transform: `translate3d(${-fromOffset}%, 0, 0)` }, { transform: 'translate3d(0, 0, 0)' }],
              timing,
            ),
          );
        }

        // Start the outgoing media's blur + overlay ramp in this same frame, NOT when
        // the tab was clicked: the wipe waits for decode first, and a ramp started
        // back there would already be finished before the mask began moving.
        // CSS transitions rather than animations on purpose — transitions outrank
        // animations in the cascade, so an animation here would be ignored.
        if (prevCell) prevCell.classList.add('fc-media-under');

        nextCell.fcWipe = anims;
        // The inline values were only the pre-start guard — the animations own them now.
        nextCell.style.transform = '';
        if (nextImg) nextImg.style.transform = '';

        anims[0].onfinish = () => {
          if (seq !== seqCounter) return;
          commit(panelId);
        };
      };

      // Wait for decode: a media that was hidden + lazy is fetched only now, and that
      // fetch/decode would otherwise land inside the first frames of the wipe.
      // The 300ms cap means a slow image delays the wipe, never blocks it.
      const decoded =
        !nextImg || nextImg.tagName !== 'IMG' || (nextImg.complete && nextImg.naturalWidth > 0)
          ? nextImg && nextImg.decode
            ? nextImg.decode().catch(() => {})
            : Promise.resolve()
          : new Promise((resolve) => {
              const done = () => resolve();
              nextImg.addEventListener('load', done, { once: true });
              nextImg.addEventListener('error', done, { once: true });
              setTimeout(done, 300);
            });

      decoded.then(() => {
        if (seq !== seqCounter) return;

        // Warm-up frame: decoding is not rasterising. Paint the media in place at ~zero
        // opacity for one frame so the compositor uploads its texture before the wipe
        // starts — otherwise that upload happens on the first visible frames and the
        // mask looks stuck.
        nextCell.style.transform = 'translate3d(0, 0, 0)';
        if (nextImg) {
          nextImg.style.transform = 'translate3d(0, 0, 0)';
          nextImg.style.opacity = '0.001';
        }

        requestAnimationFrame(() => {
          if (seq !== seqCounter) return;
          // Back to the closed mask, then start on the next clean frame.
          nextCell.style.transform = `translate3d(${fromOffset}%, 0, 0)`;
          if (nextImg) {
            nextImg.style.transform = `translate3d(${-fromOffset}%, 0, 0)`;
            nextImg.style.opacity = '';
          }
          requestAnimationFrame(startWipe);
        });
      });
    };

    // Direction comes from the tab index: higher = next, lower = prev.
    let activeIndex = tabArr.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    if (activeIndex < 0) activeIndex = 0;

    tabArr.forEach((tab) => {
      const tabObserver = new MutationObserver(() => {
        if (tab.getAttribute('aria-selected') !== 'true') return;
        const panelId = tab.getAttribute('aria-controls');
        if (!panelId) return;
        const newIndex = tabArr.indexOf(tab);
        const goingNext = newIndex >= activeIndex;
        activeIndex = newIndex;
        reveal(panelId, goingNext);
      });
      tabObserver.observe(tab, { attributes: true, attributeFilter: ['aria-selected'] });
    });

    // Normalise once at startup: a theme-editor re-render or a page restored from the
    // back/forward cache can leave more than one cell visible.
    commit(activeId);
  });

  const testimonialSection = document.querySelector('.testimonial_product');
  if (testimonialSection) {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animatePanel(testimonialSection, 'bottomToTop', true);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );
    observer.observe(testimonialSection);
  }  

  const introBlock = document.querySelector('.intro-block');
  if (introBlock) {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animatePanel(introBlock, 'rightToLeft', true);
            obs.unobserve(entry.target);
          }
        });
      },
      // Same early-fire as the panel observer above: hide off-screen, reveal
      // in view. See the comment there for why.
      { threshold: 0, rootMargin: '0px 0px 12% 0px' },
    );
    observer.observe(introBlock);
  }
});

/**homepage slider animation */
function safeNodes(list) {
  return Array.from(list || []).filter((el) => {
    try {
      return (
        el &&
        el.isConnected &&
        getComputedStyle(el).display !== 'none' &&
        (el.offsetWidth > 0 || el.offsetHeight > 0 || getComputedStyle(el).position === 'fixed')
      );
    } catch (e) {
      return false;
    }
  });
}
function safeAnimate(targets, keyframes, options) {
  const nodes = safeNodes(targets);
  if (!nodes.length) return null;
  return animate7(nodes, keyframes, options);
}
function findActiveSlide(slideshow) {
  if (!slideshow) return null;
  return (
    slideshow.querySelector('.slideshow__slide.is-selected') ||
    slideshow.querySelector('.slideshow__slide.is-active') ||
    Array.from(slideshow.querySelectorAll('.slideshow__slide')).find((s) => {
      try {
        const st = getComputedStyle(s);
        return (
          st.display !== 'none' &&
          (s.classList.contains('is-selected') || (s.getAttribute('style') || '').includes('translateX(0'))
        );
      } catch (e) {
        return false;
      }
    }) ||
    slideshow.querySelector('.slideshow__slide') ||
    null
  );
}
function runHomeBannerAnimationsForSlide(slide) {
  if (!slide || !slide.isConnected) return;
  const slideshowRoot = slide.closest('.homebanner_slider') || document.querySelector('.homebanner_slider');
  if (!slideshowRoot) return;
  const proseContainer = slide.querySelector('.prose');
  const controlsRoot = slideshowRoot.querySelector('.slideshow__controls');
  const bgMedia = slide.querySelector('.content-box__background-media');
  const proseElements = proseContainer ? Array.from(proseContainer.querySelectorAll(':scope > *')) : [];
  const controlElements = controlsRoot ? Array.from(controlsRoot.querySelectorAll(':scope > *')) : [];
  const allElements = [...proseElements, ...controlElements];

  if (bgMedia) {
    const bgChildren = Array.from(bgMedia.querySelectorAll(':scope > *'));
    bgChildren.forEach((el) => {
      try {
        el.style.opacity = '0';
        el.style.transform = 'scale(0.5)';
      } catch (e) {}
    });

    setTimeout(() => {
      safeAnimate(
        safeNodes(bgChildren),
        { opacity: [0, 1], transform: ['scale(0.5)', 'scale(1)'] },
        {
          duration: 1.2,
          ease: 'easeOut',
          delay: stagger(0.15),
        },
      );
    }, 50);
  }

  allElements.forEach((el) => {
    try {
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
    } catch (e) {}
  });

  setTimeout(() => {
    safeAnimate(
      safeNodes(allElements),
      { opacity: [0, 1], transform: ['translateY(20px)', 'translateY(0)'] },
      {
        duration: 0.5,
        ease: 'easeOut',
        delay: stagger(0.35),
      },
    );
  }, 1000);
}

document.addEventListener('DOMContentLoaded', () => {
  const slideshow = document.querySelector('.homebanner_slider');
  if (!slideshow) return;

  setTimeout(() => {
    const first = findActiveSlide(slideshow);
    if (first) runHomeBannerAnimationsForSlide(first);
  }, 120);

  ['slideChange', 'slide-change', 'slideshow:changed', 'slideshow:change', 'change'].forEach((ev) => {
    slideshow.addEventListener(ev, (e) => {
      const newSlide = e?.detail?.slide || findActiveSlide(slideshow);
      if (newSlide) runHomeBannerAnimationsForSlide(newSlide);
    });
  });

  const slides = Array.from(slideshow.querySelectorAll('.slideshow__slide'));
  if (slides.length) {
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'class') {
          const target = m.target;
          if (
            target.classList &&
            (target.classList.contains('is-selected') || target.classList.contains('is-active'))
          ) {
            runHomeBannerAnimationsForSlide(target);
            break;
          }
        }
      }
    });
    slides.forEach((s) => mo.observe(s, { attributes: true, attributeFilter: ['class'] }));
  }

  slideshow.addEventListener('click', (e) => {
    if (
      e.target.closest('.scroll-marker, .carousel-navigation button, .slideshow__controls button, .carousel-navigation')
    ) {
      setTimeout(() => {
        const newSlide = findActiveSlide(slideshow);
        if (newSlide) runHomeBannerAnimationsForSlide(newSlide);
      }, 120);
    }
  });
});

/**Blog section animation */
document.addEventListener('DOMContentLoaded', () => {
  const homepageBlog = document.querySelector('.homepage_blog');
  if (homepageBlog) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            homepageBlog.classList.add('is-visible');
          } else {
            homepageBlog.classList.remove('is-visible');
          }
        });
      },
      { threshold: 0.5 },
    );
    observer.observe(homepageBlog);
  }

  const blogCarousel = document.querySelector('.homepage_blog');
  if (blogCarousel) {
    const nav = blogCarousel.querySelector('carousel-navigation');
    const navButtons = nav ? Array.from(nav.querySelectorAll('.scroll-marker')) : [];
    const slides = Array.from(blogCarousel.querySelectorAll('.slideshow__slide'));

    function animateBlogImageAtIndex(index) {
      const slide = slides[index];
      if (!slide) return;
      const imgContainer = slide.querySelector('.blog-post-card__image-container');
      if (imgContainer) {
        imgContainer.style.opacity = '0';
        imgContainer.style.clipPath = 'inset(0 100% 0 0)';
        requestAnimationFrame(() => {
          animate7(
            [imgContainer],
            {
              opacity: [0, 1],
              clipPath: ['inset(0 100% 0 0)', 'inset(0 0 0 0)'],
            },
            { duration: 0.9, ease: 'easeInOut' },
          );
        });
      }

      const addonContainer = slide.querySelector('.blog-post-card__add-on-image');
      if (addonContainer) {
        addonContainer.style.opacity = '0';
        addonContainer.style.clipPath = 'inset(0 0 0 100%)';
        requestAnimationFrame(() => {
          animate7(
            [addonContainer],
            {
              opacity: [0, 1],
              clipPath: ['inset(0 0 0 100%)', 'inset(0 0 0 0)'],
            },
            { duration: 0.9, ease: 'easeInOut' },
          );
        });
      }
    }

    if (nav && navButtons.length) {
      const mo = new MutationObserver(() => {
        const activeBtn = nav.querySelector('[aria-current="true"]');
        const idx = activeBtn ? navButtons.indexOf(activeBtn) : -1;
        if (idx !== -1) animateBlogImageAtIndex(idx);
      });

      navButtons.forEach((btn) => mo.observe(btn, { attributes: true, attributeFilter: ['aria-current'] }));

      navButtons.forEach((btn, i) => {
        btn.addEventListener('click', () => {
          setTimeout(() => animateBlogImageAtIndex(i), 50);
        });
      });
    }
  }
});

class VariantPagination extends HTMLElement {
  connectedCallback() {
    this.perPage = parseInt(this.dataset.perPage, 10) || 20;
    this.productList = this.querySelector('product-list');
    this.nav = this.querySelector('[data-pagination-nav]');
    if (!this.productList || !this.nav) return;

    this.mobileColumns = parseInt(this.dataset.mobileColumns, 10) || 1;
    this.desktopColumns = parseInt(this.dataset.desktopColumns, 10) || 4;
    this.desktopQuery = window.matchMedia('(min-width: 700px)');

    /* Promo tiles share the grid with the cards, so a page holds `perPage` SLOTS rather than
       `perPage` cards: a tile that covers two columns leaves room for two fewer cards. Tiles are
       toggled via style because their own `display: grid` (theme.css) beats [hidden]. */
    this.items = Array.from(this.productList.children).map((el) => ({
      el: el,
      isPromo: el.classList.contains('product-list__promo'),
      page: 1,
    }));

    this.paginate();
    if (this.totalPages <= 1) return;

    this.currentPage = this.readPageFromUrl();
    this.render();

    window.addEventListener('popstate', () => {
      this.currentPage = this.readPageFromUrl();
      this.render();
    });

    /* Slot cost changes with the breakpoint, so the pages have to be recut when it flips */
    this.desktopQuery.addEventListener('change', () => {
      this.paginate();
      this.currentPage = Math.min(this.currentPage, this.totalPages);
      this.render();
    });
  }

  /* Grid slots one item occupies: cards always one, a promo its column span (full row on mobile,
     where the tile is `grid-column: 1 / -1`) multiplied by its row span. */
  slotsFor(item) {
    if (!item.isPromo) return 1;

    const size = item.el.className.match(/product-list__promo--size-(\d+)_(\d+)/);
    const columns = this.desktopQuery.matches ? Math.min(parseInt(size?.[1], 10) || 1, this.desktopColumns) : this.mobileColumns;
    const rows = this.desktopQuery.matches ? parseInt(size?.[2], 10) || 1 : 1;

    return columns * rows;
  }

  /* Fill each page up to `perPage` slots, so every page renders the same number of grid cells */
  paginate() {
    let page = 1;
    let used = 0;

    for (const item of this.items) {
      const slots = this.slotsFor(item);

      if (used > 0 && used + slots > this.perPage) {
        page++;
        used = 0;
      }

      item.page = page;
      used += slots;
    }

    this.totalPages = Math.max(1, page);
  }

  readPageFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get('vpage'), 10);
    if (Number.isNaN(page) || page < 1) return 1;
    return Math.min(page, this.totalPages);
  }

  render() {
    this.items.forEach((item) => {
      if (item.isPromo) {
        item.el.style.display = item.page === this.currentPage ? '' : 'none';
      } else {
        item.el.hidden = item.page !== this.currentPage;
      }
    });
    this.renderNav();
  }

  renderNav() {
    const nav = this.nav;
    nav.innerHTML = '';
    nav.hidden = false;

    if (this.currentPage > 1) {
      nav.appendChild(this.makeArrowLink(this.currentPage - 1, 'prev'));
    }

    for (let p = 1; p <= this.totalPages; p++) {
      if (p === this.currentPage) {
        const span = document.createElement('span');
        span.className = 'pagination__link subheading';
        span.setAttribute('aria-current', 'page');
        span.textContent = p;
        nav.appendChild(span);
      } else {
        nav.appendChild(this.makePageLink(p));
      }
    }

    if (this.currentPage < this.totalPages) {
      nav.appendChild(this.makeArrowLink(this.currentPage + 1, 'next'));
    }
  }

  makePageLink(page) {
    const a = document.createElement('a');
    a.className = 'pagination__link subheading';
    a.href = this.buildUrl(page);
    a.textContent = page;
    a.addEventListener('click', (e) => {
      e.preventDefault();
      this.goToPage(page);
    });
    return a;
  }

  makeArrowLink(page, rel) {
    const a = document.createElement('a');
    a.className = 'circle-button circle-button--lg';
    a.rel = rel;
    a.href = this.buildUrl(page);
    a.setAttribute('aria-label', `Go to page ${page}`);
    const d = rel === 'prev'
      ? 'M35 18H3M19.5 34.5 3 18 19.5 1.5'
      : 'M1 18h32M16.5 1.5 33 18 16.5 34.5';
    a.innerHTML = `<svg aria-hidden="true" focusable="false" width="13" part="icon" viewBox="0 0 36 36"><path fill="none" stroke="currentColor" stroke-width="2" d="${d}"/></svg>`;
    a.addEventListener('click', (e) => {
      e.preventDefault();
      this.goToPage(page);
    });
    return a;
  }

  buildUrl(page) {
    const url = new URL(window.location.href);
    if (page === 1) url.searchParams.delete('vpage');
    else url.searchParams.set('vpage', page);
    url.hash = '';
    return url.toString();
  }

  goToPage(page) {
    this.currentPage = page;
    history.pushState({ page }, '', this.buildUrl(page));
    this.render();
    const target = document.getElementById('collection');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (!window.customElements.get('variant-pagination')) {
  window.customElements.define('variant-pagination', VariantPagination);
}

/* Sorting or filtering must send the variant grid back to page 1. <facet-link merge-params>
   rebuilds the target URL from window.location, so vpage survives the sort unless we drop it
   first — capture phase runs before FacetLink's own listener reads the location. */
(function () {
  document.addEventListener(
    'click',
    function (event) {
      if (!event.target.closest || !event.target.closest('facet-link')) return;
      if (!document.querySelector('variant-pagination')) return;

      var params = new URLSearchParams(window.location.search);
      if (!params.has('vpage')) return;

      params.delete('vpage');
      var search = params.toString();
      history.replaceState(
        history.state,
        '',
        window.location.pathname + (search ? '?' + search : '') + window.location.hash
      );
    },
    true
  );
})();

// Change the product gallery main image on thumbnail hover (desktop only) by firing the thumbnail's existing click navigation.
(function () {
  var lastHovered = null;
  document.addEventListener('mouseover', function (event) {
    if (window.matchMedia('(hover: none)').matches) return;
    var marker = event.target.closest('.product-gallery__thumbnail-list .scroll-marker');
    if (!marker || marker === lastHovered || marker.hidden) return;
    lastHovered = marker;
    // Flag hover-driven nav so the thumbnail strip does NOT auto-scroll to center
    // the marker (that would move a new thumb under the pointer → runaway loop).
    // Cleared on the next tick, after the click's centerSelected has had its chance.
    var gallery = marker.closest('product-gallery');
    if (gallery) {
      gallery.__pgHoverNav = true;
      setTimeout(function () { gallery.__pgHoverNav = false; }, 0);
    }
    marker.click();
  });
})();

// Keep the quick-buy modal open when interacting with the global back-in-stock popup, which lives on <body> (outside the modal's DOM) so its clicks would otherwise count as "outside" and close the modal / get focus-trapped.
(function () {
  function patch() {
    var QB = window.customElements.get('quick-buy-modal');
    if (!QB || QB.__ptBisPatched) return true;
    QB.__ptBisPatched = true;
    var inBis = function (t) { return !!(t && t.closest && t.closest('#pt-bis-modal')); };
    var origHide = QB.prototype.hideForOutsideClickTarget;
    var origAllow = QB.prototype.allowOutsideClickForTarget;
    QB.prototype.hideForOutsideClickTarget = function (target) {
      if (inBis(target)) return false;
      return origHide.call(this, target);
    };
    QB.prototype.allowOutsideClickForTarget = function (target) {
      if (inBis(target)) return true;
      return origAllow.call(this, target);
    };
    return true;
  }
  if (!patch()) {
    window.customElements.whenDefined('quick-buy-modal').then(patch);
  }
})();

// Quick-buy gallery prev/next. The gallery is cloned from a <template>, so its inline reveal/nav controller never runs in the modal. Drive navigation here by moving the active slide marker (.pg-active); custom.css reveals only the active slide. Slides are queried fresh each time so it keeps working after a variant change re-renders the gallery (the new markup re-supplies .is-initial as the fallback).
(function () {
  function visibleSlides(gallery) {
    return Array.prototype.filter.call(gallery.querySelectorAll('.product-gallery__media'), function (el) {
      return !el.hasAttribute('hidden');
    });
  }

  function activeIndex(list) {
    var i = list.findIndex(function (el) {
      return el.classList.contains('pg-active') || el.classList.contains('is-initial');
    });
    return i < 0 ? 0 : i;
  }

  function showSlide(gallery, list, idx) {
    if (idx < 0 || idx >= list.length) return;
    gallery.querySelectorAll('.product-gallery__media').forEach(function (el) {
      el.classList.remove('pg-active', 'is-initial');
    });
    list[idx].classList.add('pg-active');
    gallery.querySelectorAll('carousel-prev-button button').forEach(function (b) { b.disabled = idx === 0; });
    gallery.querySelectorAll('carousel-next-button button').forEach(function (b) { b.disabled = idx === list.length - 1; });
    
    gallery.querySelectorAll('.scroll-marker-group').forEach(function (group) {
      var i = 0;
      group.querySelectorAll('.scroll-marker').forEach(function (m) {
        if (m.hasAttribute('hidden')) return;
        m.setAttribute('aria-current', i === idx ? 'true' : 'false');
        i++;
      });
    });
  }

 
  // Swipe / drag. The `allow-drag` attribute is no use here: scroll-carousel implements it by
  // dragging scrollLeft, and this carousel renders one slide at a time (the rest are display:none)
  // with scrollTo/scrollBy stubbed — there is nothing to scroll. So translate the gesture into a
  // slide change directly, matching the PDP reveal script's 50px horizontal threshold.
  function addSwipe(gallery, carousel) {
    var startX = null;
    var startY = null;
    var THRESHOLD = 50;

    function onStart(e) {
      var pt = e.touches ? e.touches[0] : e;
      startX = pt.clientX;
      startY = pt.clientY;
    }

    function onEnd(e) {
      if (startX === null) return;
      var pt = e.changedTouches ? e.changedTouches[0] : e;
      var dx = startX - pt.clientX;
      var dy = startY - pt.clientY;
      startX = null;
      startY = null;
      // Ignore gestures that are mostly vertical — that is a page scroll, not a swipe.
      if (Math.abs(dx) <= Math.abs(dy) || Math.abs(dx) < THRESHOLD) return;
      var list = visibleSlides(gallery);
      if (list.length < 2) return;
      showSlide(gallery, list, activeIndex(list) + (dx > 0 ? 1 : -1));
    }

    carousel.addEventListener('touchstart', onStart, { passive: true });
    carousel.addEventListener('touchend', onEnd, { passive: true });
    carousel.addEventListener('mousedown', onStart);
    carousel.addEventListener('mouseup', onEnd);
    carousel.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });
  }

  function enableNav(gallery) {
    var carousel = gallery.querySelector('scroll-carousel');
    if (!carousel) return;
    if (!carousel.__qbNavPatched) {
      carousel.__qbNavPatched = true;
      Object.defineProperty(carousel, 'isScrollable', {
        get: function () {
          return visibleSlides(gallery).length > 1;
        },
        configurable: true,
      });
      carousel.scrollTo = function () {};
      carousel.scrollBy = function () {};
      addSwipe(gallery, carousel);
    }
    var multiple = visibleSlides(gallery).length > 1;
    gallery.querySelectorAll('carousel-prev-button, carousel-next-button').forEach(function (btn) {
      btn.toggleAttribute('hidden', !multiple);
    });
  }

  
  document.addEventListener('dialog:after-show', function (e) {
    var modal = e.target;
    if (!modal || !modal.matches || !modal.matches('quick-buy-modal')) return;

    function sync() {
      var gallery = modal.querySelector('product-gallery');
      if (!gallery) return;
      enableNav(gallery);
      var list = visibleSlides(gallery);
      if (list.length) showSlide(gallery, list, activeIndex(list));
    }

    sync();

    // childList only: sync() itself changes classes/attributes, so watching attributes
    // would feed back into an endless loop.
    if (!modal.__qbGalleryObserver) {
      modal.__qbGalleryObserver = new MutationObserver(sync);
    }
    modal.__qbGalleryObserver.observe(modal, { childList: true, subtree: true });
  }, true);

  document.addEventListener('dialog:after-hide', function (e) {
    var modal = e.target;
    if (modal && modal.__qbGalleryObserver) modal.__qbGalleryObserver.disconnect();
  }, true);

  // Capture arrow clicks before the (inert) scroll-carousel handlers and own the transition.
  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest('quick-buy-modal')) return;
    var navEl = e.target.closest('carousel-prev-button, carousel-next-button');
    if (!navEl) return;
    var gallery = navEl.closest('product-gallery');
    if (!gallery) return;
    e.preventDefault();
    e.stopPropagation();
    var list = visibleSlides(gallery);
    if (!list.length) return;
    var dir = navEl.matches('carousel-next-button') ? 1 : -1;
    showSlide(gallery, list, activeIndex(list) + dir);
  }, true);

  // Same for the slide-indicator bars: carousel-navigation would scroll the carousel,
  // but the modal renders one slide at a time rather than scrolling, so jump directly.
  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest('quick-buy-modal')) return;
    var marker = e.target.closest('.scroll-marker');
    if (!marker) return;
    var group = marker.closest('carousel-navigation');
    var gallery = marker.closest('product-gallery');
    if (!group || !gallery) return;
    var markers = Array.prototype.filter.call(group.querySelectorAll('.scroll-marker'), function (m) {
      return !m.hasAttribute('hidden');
    });
    var idx = markers.indexOf(marker);
    if (idx < 0) return;
    e.preventDefault();
    e.stopPropagation();
    var list = visibleSlides(gallery);
    if (list.length) showSlide(gallery, list, idx);
  }, true);
})();

// While the back-in-stock popup is open over a quick-buy modal, add it to the modal's focus trap so its email field can be focused/typed (the trap would otherwise pull focus back into the modal).
(function () {
  function watch() {
    var bis = document.getElementById('pt-bis-modal');
    if (!bis) return false;
    new MutationObserver(function () {
      var qb = document.querySelector('quick-buy-modal[open]');
      if (!qb || !qb.focusTrap) return;
      qb.focusTrap.updateContainerElements(bis.hidden ? qb : [qb, bis]);
    }).observe(bis, { attributes: true, attributeFilter: ['hidden'] });
    return true;
  }
  if (!watch()) {
    document.addEventListener('DOMContentLoaded', watch);
  }
})();

// Grid / List toggle for color swatch pickers. Both views render the same-named radios, so native
// radio grouping keeps a single value checked; on toggle we re-assert the visible view's radio.
(function () {
  function getChecked(vp) {
    return vp.querySelector('input[data-option-position]:checked');
  }

  function refreshTrigger(vp) {
    var trigger = vp.querySelector('.variant-list__trigger');
    var checked = getChecked(vp);
    if (!trigger || !checked) return;

    var input = vp.querySelector('.picker-view--list input[value="' + CSS.escape(checked.value) + '"]');
    var row = input ? input.closest('.variant-list__row') : null;
    if (!row) return;

    var triggerSwatch = trigger.querySelector('.variant-list__swatch');
    var rowSwatch = row.querySelector('.variant-list__swatch');
    var triggerCat = trigger.querySelector('.variant-list__category');
    var triggerName = trigger.querySelector('.variant-list__name');
    var rowCat = row.querySelector('.variant-list__category');
    var rowName = row.querySelector('.variant-list__name');

    if (triggerSwatch && rowSwatch) triggerSwatch.innerHTML = rowSwatch.innerHTML;
    if (triggerCat) triggerCat.textContent = rowCat ? rowCat.textContent : '';
    if (triggerName) triggerName.textContent = rowName ? rowName.textContent : '';
  }

  function syncView(vp, view) {
    var checked = getChecked(vp);
    if (checked) {
      var target = vp.querySelector('.picker-view--' + view + ' input[data-option-position][value="' + CSS.escape(checked.value) + '"]');
      if (target && !target.checked) target.checked = true;
    }
    if (view === 'grid') {
      if (typeof window.updateActiveClass === 'function') window.updateActiveClass();
    } else {
      refreshTrigger(vp);
    }
  }

  // Remember the chosen view per form so it survives variant-change re-renders.
  var viewByForm = {};

  function applyView(vp, view) {
    vp.setAttribute('data-active-view', view);
    vp.querySelectorAll('.view-toggle__btn').forEach(function (b) {
      var active = b.dataset.view === view;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-pressed', active);
    });
    syncView(vp, view);
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('.view-toggle__btn');
    if (!btn) return;
    var vp = btn.closest('variant-picker');
    if (!vp) return;

    var formId = vp.getAttribute('form-id');
    if (formId) viewByForm[formId] = btn.dataset.view;
    applyView(vp, btn.dataset.view);
  });

  // Keep the list trigger label in sync when a shade is picked from the list.
  document.addEventListener('change', function (event) {
    var input = event.target.closest('.picker-view--list input[data-option-position]');
    if (input) refreshTrigger(input.closest('variant-picker'));
  });

  // The base theme replaces the variant-picker block on variant change, resetting it to the
  // default Grid view — re-apply the customer's chosen view once the new HTML is in place.
  document.addEventListener('variant:change', function () {
    document.querySelectorAll('variant-picker[form-id]').forEach(function (vp) {
      var view = viewByForm[vp.getAttribute('form-id')];
      if (view && vp.getAttribute('data-active-view') !== view) applyView(vp, view);
    });
  });
})();

// Custom scrollbar for the variant list popover. Hides the native bar (via CSS) and drives a
// floating inset thumb whose size/position track the viewport's scroll, matching the Figma design.
(function () {
  if (window.customElements.get('variant-list-scroll')) return;

  var VariantListScroll = class extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-scroll-viewport]');
      this.track = this.querySelector('[data-scroll-track]');
      this.thumb = this.querySelector('[data-scroll-thumb]');
      if (!this.viewport || !this.track || !this.thumb) return;

      this._onScroll = this.sync.bind(this);
      this._onThumbDown = this.startDrag.bind(this);

      this.viewport.addEventListener('scroll', this._onScroll, { passive: true });
      this.thumb.addEventListener('pointerdown', this._onThumbDown);

      // Recompute when the viewport is resized (fonts load, panel opens, content re-renders).
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(this._onScroll);
        this._ro.observe(this.viewport);
      }

      this.sync();
    }

    disconnectedCallback() {
      if (this.viewport) this.viewport.removeEventListener('scroll', this._onScroll);
      if (this.thumb) this.thumb.removeEventListener('pointerdown', this._onThumbDown);
      if (this._ro) this._ro.disconnect();
    }

    // Size + position the thumb from the viewport's scroll metrics.
    sync() {
      // Custom bar is desktop-only (CSS hides it <1000px); skip work on mobile native scroll.
      if (!window.matchMedia('(min-width: 1000px)').matches) {
        this.track.classList.remove('is-scrollable');
        return;
      }

      var vp = this.viewport;
      var trackH = this.track.clientHeight;
      var scrollable = vp.scrollHeight - vp.clientHeight;

      if (scrollable <= 1 || trackH <= 0) {
        this.track.classList.remove('is-scrollable');
        return;
      }
      this.track.classList.add('is-scrollable');

      var ratio = vp.clientHeight / vp.scrollHeight;
      var thumbH = Math.max(trackH * ratio, 24); // clamp to CSS min-height (1.5rem)
      var maxOffset = trackH - thumbH;
      var offset = (vp.scrollTop / scrollable) * maxOffset;

      this.thumb.style.height = thumbH + 'px';
      this.thumb.style.transform = 'translateY(' + offset + 'px)';
    }

    startDrag(event) {
      event.preventDefault();
      var vp = this.viewport;
      var trackH = this.track.clientHeight;
      var thumbH = this.thumb.offsetHeight;
      var scrollable = vp.scrollHeight - vp.clientHeight;
      var maxOffset = trackH - thumbH;
      var startY = event.clientY;
      var startScroll = vp.scrollTop;

      var move = function (e) {
        if (maxOffset <= 0) return;
        var delta = e.clientY - startY;
        vp.scrollTop = startScroll + (delta / maxOffset) * scrollable;
      };
      var up = function () {
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', up);
      };
      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', up);
    }
  };

  window.customElements.define('variant-list-scroll', VariantListScroll);

  // The popover starts hidden (display:none), so scrollHeight is 0 until it opens — resync then.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('.variant-list__trigger');
    if (!trigger) return;
    var popover = document.getElementById(trigger.getAttribute('aria-controls'));
    var scroller = popover && popover.querySelector('variant-list-scroll');
    if (scroller) requestAnimationFrame(function () { scroller.sync(); });
  });
})();

(function () {
  // Self-contained Swym wishlist buttons (product cards + inline hearts) driven by the Swym API,
  // independent of the app's auto-injected collections module.
  if (window.__swymInlineWishlistInit) return;
  window.__swymInlineWishlistInit = true;
  window.__swymWishlistEpis = window.__swymWishlistEpis || {};

  var SELECTOR = '.swym-inline-wishlist, .product-card__wishlist';

  // Reflect cached wishlist status on every button (keyed by variant id).
  function swymSyncInlineButtons() {
    document.querySelectorAll(SELECTOR).forEach(function (btn) {
      var added = !!window.__swymWishlistEpis[btn.dataset.epi];
      btn.classList.toggle('is-added', added);
      btn.classList.toggle('swym-added', added);
    });
  }

  // Pull the current wishlist from Swym once it's ready.
  function swymLoadWishlist() {
    if (!window._swat || typeof window._swat.fetch !== 'function') return;
    window._swat.fetch(function (items) {
      window.__swymWishlistEpis = {};
      (items || []).forEach(function (it) { window.__swymWishlistEpis[it.epi] = true; });
      swymSyncInlineButtons();
    }, function () {});
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function () { swymLoadWishlist(); });
  swymLoadWishlist();

  // Section re-renders on variant change wipe the class; re-apply the cached status.
  document.addEventListener('variant:change', function () {
    setTimeout(swymSyncInlineButtons, 300);
  });

  // variant:change only fires for same-product switches and product:rerender doesn't bubble, so
  // neither is a reliable hook inside the quick-buy modal. Watch for freshly inserted buttons
  // instead — that covers every re-render path (modal, PDP, quick buy, paginated cards).
  var syncQueued = false;
  function queueSync() {
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(function () {
      syncQueued = false;
      swymSyncInlineButtons();
    });
  }

  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (!node || node.nodeType !== 1) continue;
        if ((node.matches && node.matches(SELECTOR)) || (node.querySelector && node.querySelector(SELECTOR))) {
          queueSync();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest(SELECTOR);
    if (!btn || !btn.dataset.epi) return;
    if (!window._swat) {
      /* BH #495: on slow mobile connections Swym's API arrives seconds after the
         hearts render, and taps in that window were silently dropped (the button
         just sat in its grey resting state). Acknowledge the tap visually and
         replay it once Swym is ready. */
      window.__swymPendingTaps = window.__swymPendingTaps || [];
      if (window.__swymPendingTaps.indexOf(btn) === -1) {
        btn.classList.add('swym-adding');
        window.__swymPendingTaps.push(btn);
      }
      window.SwymCallbacks = window.SwymCallbacks || [];
      if (!window.__swymTapFlushBound) {
        window.__swymTapFlushBound = true;
        window.SwymCallbacks.push(function () {
          var pending = window.__swymPendingTaps || [];
          window.__swymPendingTaps = [];
          pending.forEach(function (b) {
            b.classList.remove('swym-adding');
            b.click();
          });
        });
      }
      return;
    }
    var epi = btn.dataset.epi;
    var product = {
      empi: parseInt(btn.dataset.empi, 10),
      epi: parseInt(epi, 10),
      du: btn.dataset.du,
      iu: btn.dataset.iu,
      dt: btn.dataset.dt
    };
    var added = btn.classList.contains('is-added');
    var onDone = function () {
      window.__swymWishlistEpis[epi] = !added;
      swymSyncInlineButtons();
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { epi: epi, added: !added } }));
    };
    if (added) {
      window._swat.removeFromWishList(product, onDone, function () {});
    } else {
      window._swat.addToWishList(product, onDone, function () {});
    }
  });

  // Swym's remote config keeps injecting the collections quick-save heart even when disabled in
  // the app; strip whatever it injects (the theme's own hearts carry no swym- class in cards).
  function stripSwymInjected() {
    document
      .querySelectorAll('product-card [class*="swym-"], [class*="swym-advanced-wishlist"], [class*="swym-wishlist-collections"]')
      .forEach(function (el) {
        if (el.closest('.swym-inline-wishlist, .product-card__wishlist')) return;
        el.remove();
      });
  }

  var stripQueued = false;
  new MutationObserver(function () {
    if (stripQueued) return;
    stripQueued = true;
    requestAnimationFrame(function () {
      stripQueued = false;
      stripSwymInjected();
    });
  }).observe(document.body, { childList: true, subtree: true });
  stripSwymInjected();
})();

/* Article chapters: builds a table of contents from the article content's H2 headings with a show/hide toggle. */
(function () {
  if (customElements.get('article-chapters')) return;

  function slugify(text, index) {
    var slug = text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
    return slug ? 'chapter-' + slug : 'chapter-' + index;
  }

  customElements.define(
    'article-chapters',
    class ArticleChapters extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.content = this.querySelector('[data-article-content]');
        this.aside = this.querySelector('.article-chapters');
        this.list = this.querySelector('.article-chapters__list');
        this.toggle = this.querySelector('.article-chapters__toggle');
        this.label = this.querySelector('.article-chapters__toggle-label');
        if (!this.content || !this.aside || !this.list || !this.toggle) return;

        var headings = Array.prototype.slice.call(this.content.querySelectorAll('h2'));
        if (!headings.length) {
          this.aside.remove();
          this.classList.remove('article-layout');
          return;
        }

        this.list.innerHTML = '';
        this.links = [];
        var used = {};
        headings.forEach(function (heading, index) {
          if (heading.textContent.trim() == "") return;
          var id = heading.id;
          if (!id) {
            id = slugify(heading.textContent, index);
            while (used[id]) id = id + '-' + index;
            heading.id = id;
          }
          used[id] = true;
          heading.style.scrollMarginTop = 'calc(var(--header-height, 0px) + 2rem)';

          var li = document.createElement('li');
          var a = document.createElement('a');
          a.href = '#' + id;
          a.className = 'article-chapters__link';
          a.textContent = heading.textContent;
          a.addEventListener('click', function (event) {
            event.preventDefault();
            heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
            history.replaceState(null, '', '#' + id);
          });
          li.appendChild(a);
          this.list.appendChild(li);
          this.links.push({ link: a, heading: heading });
        }, this);

        this.toggle.addEventListener('click', this.onToggle.bind(this));
        this.aside.hidden = false;
        this.setupScrollSpy();
      }

      onToggle() {
        var collapsed = this.classList.toggle('is-chapters-collapsed');
        this.toggle.setAttribute('aria-expanded', String(!collapsed));
        if (this.label) this.label.textContent = collapsed ? 'Show Chapters' : 'Hide Chapters';
      }

      setupScrollSpy() {
        if (!('IntersectionObserver' in window)) return;
        var self = this;
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              self.links.forEach(function (item) {
                item.link.classList.toggle('is-active', item.heading === entry.target);
              });
            });
          },
          { rootMargin: '0px 0px -70% 0px', threshold: 0 }
        );
        this.links.forEach(function (item) {
          observer.observe(item.heading);
        });
      }
    }
  );
})();

/* Copy-link share button: copies the target URL to the clipboard and flashes a "copied" state. */
(function () {
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-copy-link]');
    if (!trigger) return;
    event.preventDefault();

    var url = trigger.getAttribute('data-copy-link');
    var done = function () {
      trigger.classList.add('is-copied');
      setTimeout(function () {
        trigger.classList.remove('is-copied');
      }, 1500);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(function () {});
    } else {
      var input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      try {
        document.execCommand('copy');
        done();
      } catch (e) {}
      input.remove();
    }
  });
})();

/* Yotpo reviews: replace numbered pagination with an appending "Load More" button.
   The widget is Vue-rendered and patches its review list in place, inserting relative to anchor
   nodes it expects to still be there, so moving or removing any of its nodes makes the next patch
   (any filter/sort/search change) throw NotFoundError and abort the render. Nothing here touches a
   node Yotpo owns: on Load More we clone the page currently on screen into our own container above
   the live list, then drive Yotpo's hidden next arrow so it renders the following page itself.
   Clones are inert copies, so their text is expanded through Yotpo's own toggle before cloning and
   the dead toggles are stripped. Filters, sort and search re-query from page 1, which drops the
   clones so only the new result set shows. */
(function () {
  var WIDGET = '#yotpo-reviews-main-widget';
  var ITEM = '.yotpo-reviews-item';
  var PAGER = '.yotpo-reviews-pagination-container';
  var READ_TOGGLES = '.yotpo-read-more, .yotpo-read-less, .yotpo-read-more-btn, .yotpo-read-less-btn';
  var READ_MORE = '.yotpo-read-more, .yotpo-read-more-btn';
  var TEXT = '.yotpo-text-container';
  /* Collapsed and expanded copies of each clone's body, so its toggle can swap between Yotpo's own two states. */
  var cloneBodies = new WeakMap();
  /* Markup captured before the capture pass toggles each review, keyed by review id: toggling
     re-renders the nodes it was read from, so a reference to them cannot be kept. */
  var collapsedBodies = {};
  var expandedBodies = {};

  /* Escape hatch for diagnosing widget breakage: ?yotpo-native-pager leaves Yotpo's own pagination
     and DOM untouched, so a broken list can be attributed to the app rather than to this override. */
  if (/[?&]yotpo-native-pager\b/.test(location.search)) {
    document.documentElement.classList.add('yotpo-native-pager');
    return;
  }

  var accumulator = null;
  var button = null;
  var count = null;
  var debounce = null;
  var holding = false;
  var apiTotal = null;
  var resetPending = false;
  var inFlight = 0;
  var deferrals = 0;
  var appendTimer = null;
  var expectingAppend = false;
  var pendingClones = null;
  var verifyTimer = null;
  var lastLiveId = null;

  function widget() {
    return document.querySelector(WIDGET);
  }

  /* Filters, sort and search all re-query Yotpo from page 1, so its own review requests tell us when
     a result set is replaced, and pagination.total gives the filtered total behind the count. */
  function isReviewsRequest(url) {
    return typeof url === 'string' && /yotpo/i.test(url) && /review|topic|filter/i.test(url);
  }

  function readPayload(data) {
    var body = (data && data.response) || data;
    if (!body || !Array.isArray(body.reviews)) return;

    var pagination = body.pagination || {};
    var total = pagination.total != null ? pagination.total : pagination.total_reviews;
    var page = pagination.page != null ? pagination.page : pagination.current_page;
    if (total == null && page == null) return;
    if (total != null) apiTotal = parseInt(total, 10);
    if (page != null && parseInt(page, 10) === 1) {
      /* A page-1 query supersedes any Load More still pending, so a sort change made right after a
         Load More click replaces the list instead of stacking onto it. */
      resetPending = true;
      expectingAppend = false;
      clearTimeout(appendTimer);
    }
  }

  /* Yotpo can paint a new list before we have finished reading the response that produced it, and a
     render read too early looks like an append. Requests are counted while in flight and collect
     waits for them; every request also releases on a timer so a stall cannot freeze the list. */
  function track() {
    inFlight++;
    var released = false;
    var release = function () {
      if (released) return;
      released = true;
      inFlight--;
      clearTimeout(debounce);
      debounce = setTimeout(collect, 60);
    };
    setTimeout(release, 5000);
    return release;
  }

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (!isReviewsRequest(url)) return nativeFetch.apply(this, arguments);

      var release = track();
      var request = nativeFetch.apply(this, arguments);
      request.then(function (response) {
        response
          .clone()
          .json()
          .then(function (data) {
            readPayload(data);
            release();
          }, release);
      }, release);
      return request;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isReviewsRequest(url)) {
      var release = track();
      this.addEventListener('load', function () {
        try {
          readPayload(JSON.parse(this.responseText));
        } catch (e) {}
      });
      this.addEventListener('loadend', release);
    }
    return nativeOpen.apply(this, arguments);
  };

  /* Total review count, read from Yotpo's bottom-line right panel ("Based on N reviews").
     The score (e.g. 4.8) lives in the left panel, so the right panel's first integer is the total. */
  function totalCount(w) {
    var el = w && w.querySelector('.yotpo-bottom-line-right-panel');
    var m = el && (el.textContent || '').replace(/,/g, '').match(/\d+/);
    return m ? parseInt(m[0], 10) : null;
  }

  /* While a filter, topic or search is active Yotpo prints "We found N matching reviews" above the
     list; reusing that number keeps our count consistent with the widget's own label. The label sits
     next to the filters rather than inside them, so we scan that whole row, skipping any long text
     (reviews) to keep the match cheap and unambiguous. */
  function matchingCount(w) {
    var anchor = w && w.querySelector('.yotpo-filters-container');
    var scope = (anchor && anchor.parentElement) || w;
    if (!scope) return null;

    var candidates = [scope].concat(Array.prototype.slice.call(scope.children));
    for (var i = 0; i < candidates.length; i++) {
      var text = candidates[i].textContent || '';
      if (text.length > 400) continue;
      var m = text.replace(/,/g, '').match(/found\s+(\d+)\s+matching/i);
      if (m) return parseInt(m[1], 10);
    }
    return null;
  }

  /* Reviews Yotpo itself has rendered — everything in the widget except our own clones. */
  function liveItems(w) {
    return Array.prototype.filter.call(w.querySelectorAll(ITEM), function (el) {
      return !accumulator || !accumulator.contains(el);
    });
  }

  function signature(el) {
    return (
      el.getAttribute('data-review-id') ||
      el.getAttribute('id') ||
      (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100)
    );
  }

  function nextArrow(w) {
    var pager = w && w.querySelector(PAGER);
    if (!pager) return null;
    var btns = pager.querySelectorAll('button, [role="button"]');
    return btns.length ? btns[btns.length - 1] : null;
  }

  function isDisabled(el) {
    return !el || el.disabled || el.getAttribute('aria-disabled') === 'true' || /disabled/i.test(el.className || '');
  }

  /* Pin the scroll position across Yotpo's auto-scroll: reset before each paint so its
     jump never becomes visible. Released once the new page lands (see collect); the frame
     cap is only a safety net so a stalled fetch can't hold scroll indefinitely. */
  function holdScroll(y) {
    holding = true;
    var frames = 0;
    (function step() {
      window.scrollTo(0, y);
      if (holding && ++frames < 240) requestAnimationFrame(step);
    })();
  }

  /* Our container goes above Yotpo's live list, the count and button below it, so accumulated pages
     read in order: clones of pages 1..N, then the live page N+1, then the controls. */
  function mount(list) {
    if (!accumulator) {
      accumulator = document.createElement('div');
      accumulator.className = 'yotpo-load-more-list';
    }
    if (accumulator.parentNode !== list.parentNode || accumulator.nextSibling !== list) {
      list.parentNode.insertBefore(accumulator, list);
    }
    if (!count) {
      count = document.createElement('div');
      count.className = 'yotpo-load-more-count';
    }
    if (count.parentNode !== list.parentNode || list.nextSibling !== count) {
      list.parentNode.insertBefore(count, list.nextSibling);
    }
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'yotpo-load-more-btn';
      button.textContent = 'Load More';
      button.addEventListener('click', onClick);
    }
    if (button.parentNode !== count.parentNode || count.nextSibling !== button) {
      count.parentNode.insertBefore(button, count.nextSibling);
    }
  }

  /* Only ever removes our own clones — Yotpo's nodes are never touched. */
  function clearClones() {
    if (!accumulator || !accumulator.firstChild) return;
    while (accumulator.firstChild) accumulator.removeChild(accumulator.firstChild);
    pendingClones = null;
  }

  function clonesShown() {
    return accumulator ? accumulator.children.length : 0;
  }

  function refreshCount(w, liveCount) {
    if (!count || !button) return;

    var shown = clonesShown() + liveCount;
    var total = matchingCount(w);
    if (total == null) total = apiTotal != null ? apiTotal : totalCount(w);

    if (total && shown) {
      count.textContent = shown + ' out of ' + total;
      count.style.display = '';
      button.style.marginTop = '8px';
    } else {
      count.style.display = 'none';
      button.style.marginTop = '';
    }
  }

  function collect() {
    var w = widget();
    if (!w) return;

    if (inFlight > 0 && deferrals < 12) {
      deferrals++;
      clearTimeout(debounce);
      debounce = setTimeout(collect, 100);
      return;
    }
    deferrals = 0;

    var items = liveItems(w);
    var firstId = items.length ? signature(items[0]) : null;

    /* Drop the clones when a different result set is on screen: either Yotpo re-queried from page 1,
       or the live page changed without us having asked for an append. Re-renders of the same page
       (a Read more toggle, a re-mount) leave the accumulated pages alone. */
    if (resetPending || (firstId !== lastLiveId && !expectingAppend && !pendingClones)) {
      clearClones();
      resetPending = false;
    }
    lastLiveId = firstId;

    if (!items.length) {
      if (count) count.style.display = 'none';
      if (button) button.style.display = 'none';
      if (holding) holding = false;
      return;
    }

    mount(items[0].parentElement);

    button.disabled = false;
    button.classList.remove('is-loading');
    button.style.display = isDisabled(nextArrow(w)) ? 'none' : '';
    refreshCount(w, items.length);

    if (holding) requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        holding = false;
      });
    });
  }

  function textContainer(root) {
    return root.querySelector(TEXT) || root.querySelector('.yotpo-review-content');
  }

  /* Yotpo's own review id, which survives a re-render; signature() does not, since it falls back to
     the text and the text is what changes when a review expands. */
  function reviewKey(el) {
    var review = el.querySelector('.yotpo-review[data-id]');
    var inner = el.querySelector('[id^="yotpo-review-item"]');
    return (review && review.getAttribute('data-id')) || (inner && inner.id) || signature(el);
  }

  /* Undo the capture pass on the live review: Yotpo renders the next page into these same components,
     so one left in the wrong state comes back that way, and without its toggle. */
  function restoreLive(el) {
    var toggle = el.querySelector(READ_TOGGLES);
    if (toggle) toggle.click();
  }

  /* Yotpo's handler does not survive cloneNode, so every toggle inside a copy is marked as ours. */
  function tagToggles(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll(READ_TOGGLES), function (toggle) {
      toggle.classList.add('yotpo-clone-toggle');
    });
  }

  /* Copy the page on screen into our container. Yotpo truncates long bodies in markup rather than with
     CSS, so the copy stores the collapsed markup captured before the expansion alongside the expanded
     one and opens in the collapsed state: both states are Yotpo's own markup, down to the wording and
     the inline position of the toggle, and onCloneToggle swaps between them. */
  function cloneLivePage(items) {
    return items.map(function (el) {
      var clone = el.cloneNode(true);
      var container = textContainer(clone);
      var key = reviewKey(el);
      var collapsed = collapsedBodies[key];
      var expanded = expandedBodies[key];
      delete collapsedBodies[key];
      delete expandedBodies[key];

      if (container && collapsed) {
        /* Captured closed, so the live review is open now and the copy carries the open markup. */
        cloneBodies.set(clone, { collapsed: collapsed, expanded: container.innerHTML });
        container.innerHTML = collapsed;
        tagToggles(container);
        restoreLive(el);
      } else if (container && expanded) {
        /* Captured open, so the copy is already the closed markup and only needs its toggle marked. */
        cloneBodies.set(clone, { collapsed: container.innerHTML, expanded: expanded });
        tagToggles(container);
        restoreLive(el);
      } else if (container) {
        /* No pair of states for this one, so no control is left behind that could not respond. */
        Array.prototype.forEach.call(container.querySelectorAll(READ_TOGGLES), function (toggle) {
          if (toggle.parentNode) toggle.parentNode.removeChild(toggle);
        });
      }

      clone.classList.add('yotpo-load-more-clone');
      accumulator.appendChild(clone);
      return clone;
    });
  }

  function onCloneToggle(e) {
    var toggle = e.target.closest && e.target.closest('.yotpo-clone-toggle');
    if (!toggle) return;

    var item = toggle.closest('.yotpo-load-more-clone');
    var body = item && cloneBodies.get(item);
    var container = item && textContainer(item);
    if (!body || !container) return;

    e.preventDefault();
    var open = item.classList.toggle('yotpo-clone-open');
    container.innerHTML = open ? body.expanded : body.collapsed;
    tagToggles(container);
  }

  /* If Yotpo never advanced the page, the clones we just added would duplicate the live list, so
     they are rolled back. */
  function scheduleVerify(expectedFirstId) {
    clearTimeout(verifyTimer);
    verifyTimer = setTimeout(function () {
      if (!pendingClones) return;

      var w = widget();
      var items = w ? liveItems(w) : [];
      if (items.length && signature(items[0]) === expectedFirstId) {
        pendingClones.forEach(function (clone) {
          if (clone.parentNode) clone.parentNode.removeChild(clone);
        });
      }
      pendingClones = null;
      collect();
    }, 2500);
  }

  function onClick() {
    var w = widget();
    var arrow = nextArrow(w);
    var items = w ? liveItems(w) : [];

    if (isDisabled(arrow)) {
      button.style.display = 'none';
      return;
    }
    if (!items.length) return;

    /* Keep the markup Yotpo has on screen, then toggle each review so the other state can be read too:
       the copy needs both to carry a working Read more. Only this click consumes the captures, so
       anything left by an earlier one is dropped first. */
    collapsedBodies = {};
    expandedBodies = {};
    items.forEach(function (el) {
      var toggle = el.querySelector(READ_TOGGLES);
      var container = textContainer(el);
      var key = reviewKey(el);
      if (!toggle || !container || !key) return;

      if (toggle.getAttribute('aria-expanded') === 'true') {
        expandedBodies[key] = container.innerHTML;
      } else {
        collapsedBodies[key] = container.innerHTML;
      }

      toggle.click();
    });

    var y = window.pageYOffset;
    /* The append window is time-boxed rather than consumed by the first render, so a page arriving
       in several paint batches lands as a whole; a page-1 response closes it early. */
    expectingAppend = true;
    clearTimeout(appendTimer);
    appendTimer = setTimeout(function () {
      expectingAppend = false;
    }, 4000);
    button.disabled = true;
    button.classList.add('is-loading');

    /* One tick for Yotpo to paint the expanded bodies before they are copied. */
    setTimeout(function () {
      var current = liveItems(widget() || w);
      if (!current.length) current = items;

      mount(current[0].parentElement);
      pendingClones = cloneLivePage(current);

      /* Read both after the clone pass: it collapses the live reviews again, so the signature has to
         describe the list as it now stands, and the re-render that follows can replace the arrow. */
      var firstId = signature(current[0]);
      var pageArrow = nextArrow(widget() || w) || arrow;

      pageArrow.click();
      holdScroll(y);
      scheduleVerify(firstId);
    }, 60);
  }

  var observer = new MutationObserver(function () {
    clearTimeout(debounce);
    debounce = setTimeout(collect, 120);
  });

  /* The Vue-based Yotpo widget lazy-mounts and can re-mount during hydration, which
     detaches any observer bound to the old node. So we never give up: a persistent watcher
     re-binds the observer whenever the widget element is (re)created, and re-runs collect. */
  var observed = null;
  var bootDebounce = null;

  function ensure() {
    var w = widget();
    if (!w) return;
    if (observed !== w) {
      observed = w;
      observer.disconnect();
      observer.observe(w, { childList: true, subtree: true });
      collect();
    }
  }

  var boot = new MutationObserver(function () {
    clearTimeout(bootDebounce);
    bootDebounce = setTimeout(ensure, 150);
  });
  boot.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('click', onCloneToggle);
  ensure();
})();

/* SEO content section: swaps the accordion copy to the selected variant's custom.seo_copy metafield (falling back to the product metafield), and hides the section when the variant has no copy. */
(function () {
  document.querySelectorAll('[data-seo-content]').forEach(function (section) {
    var mapEl = section.querySelector('[data-seo-copy-map]');
    if (!mapEl) return;

    var map;
    try {
      map = JSON.parse(mapEl.textContent);
    } catch (e) {
      return;
    }

    function apply(variantId) {
      if (variantId == null) return;
      var html = map[String(variantId)];
      if (html && html.trim() !== '') {
        var content = section.querySelector('.accordion__content');
        if (content) content.innerHTML = html;
        section.style.display = '';
      } else {
        section.style.display = 'none';
      }
    }

    document.addEventListener('variant:change', function (e) {
      var variant = e.detail && e.detail.variant;
      if (variant) apply(variant.id);
    });
  });
})();


/* Sticky add-to-cart variant selector: mirror a sticky option choice onto the
   main variant picker input so the variant actually changes. */
(function () {
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches('[data-sticky-option]')) return;

    var wrapper = input.closest('[data-sticky-variant]');
    var formId = wrapper && wrapper.getAttribute('data-form-id');
    if (!formId) return;

    var valueName = input.getAttribute('data-option');

    // Bundle products: the visible component swatches drive a hidden picker via
    // their own change handler. Trigger the matching bundle swatch (matched by
    // option value name) so both the visible selection and the cart update.
    if (valueName) {
      var bundleInputs = document.querySelectorAll('.bundle-product-card input[data-option-position]');
      for (var i = 0; i < bundleInputs.length; i++) {
        var bundleInput = bundleInputs[i];
        if (
          bundleInput.name.indexOf(formId + '-bundle-') === 0 &&
          bundleInput.getAttribute('data-option') === valueName
        ) {
          if (!bundleInput.checked) {
            bundleInput.checked = true;
            bundleInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
      }
    }

    // Standard products: drive the theme variant picker by option value id.
    var picker = document.querySelector('variant-picker[form-id="' + formId + '"]');
    if (!picker) return;

    var position = input.getAttribute('data-option-position');
    var target = picker.querySelector(
      'input[data-option-position="' + position + '"][value="' + input.value + '"]'
    );
    if (target && !target.checked) {
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
})();

/* Add-to-cart errors ("The maximum quantity of this item is already in your
   cart.", inventory refusals, bundle validation) only reach the shopper as a
   2.5s toast pinned to the bottom of the viewport, which is easy to miss. Mirror
   the form's cart:error into a persistent message under the Add to bag button. */
(function () {
  var ICON =
    '<svg width="13" height="13" viewBox="0 0 13 13" aria-hidden="true" focusable="false">' +
    '<circle cx="6.5" cy="6.5" r="6.5" fill="currentColor"/>' +
    '<path fill="#fff" d="M5.874 3.528l.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"/>' +
    '</svg>';

  function container(node) {
    if (!node || !node.querySelector) return null;
    var form = node.closest ? node.closest('form[action*="/cart/add"]') : null;
    return (form || node).querySelector('[data-cart-error]');
  }

  function clear(event) {
    var target = container(event.target);
    if (target) target.replaceChildren();
  }

  document.addEventListener('cart:error', function (event) {
    var target = container(event.target);
    if (!target) return;

    var message = event.detail && event.detail.error;
    if (!message) {
      target.replaceChildren();
      return;
    }

    var paragraph = document.createElement('p');
    paragraph.className = 'h-stack gap-1.5 text-sm text-error';
    paragraph.setAttribute('role', 'alert');
    paragraph.innerHTML = ICON;
    paragraph.appendChild(document.createTextNode(' ' + message));
    target.replaceChildren(paragraph);
  });

  // A successful add or a variant switch makes the previous error stale.
  document.addEventListener('variant:add', clear);
  document.addEventListener('variant:change', clear);
})();

/* Sticky add-to-cart quantity: its input is deliberately outside the product
   form (a duplicate name="quantity" would win over the real one), so mirror the
   value between the sticky and the main quantity selector. */
(function () {
  var syncing = false;

  function apply(selector, value) {
    if (!selector) return;
    syncing = true;
    if ('quantity' in selector) {
      selector.quantity = value;
    } else {
      selector.querySelector('input').value = value;
    }
    syncing = false;
  }

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (syncing || !input.matches || !input.matches('quantity-selector input[name="quantity"]')) return;

    var sticky = input.closest('product-sticky-add-to-cart');
    if (sticky) {
      var mainForm = document.getElementById(sticky.getAttribute('main-product-form'));
      apply(mainForm && mainForm.querySelector('quantity-selector'), input.value);
      return;
    }

    var form = input.form;
    if (!form || !form.id) return;
    var stickyBar = document.querySelector('product-sticky-add-to-cart[main-product-form="' + form.id + '"]');
    apply(stickyBar && stickyBar.querySelector('quantity-selector'), input.value);
  });
})();

/* Vertically center Klaviyo's Back-in-Stock popup. It renders in a same-origin
   #klaviyo-bis-iframe we can't reach with theme CSS, so inject centering into
   the iframe document and re-apply on Klaviyo's re-renders. */
(function () {
  var IFRAME_ID = 'klaviyo-bis-iframe';

  var CSS =
    'html{height:100%!important;margin:0!important}' +
    'body{margin:0!important;min-height:100vh!important;display:flex!important;' +
    'flex-direction:column!important;align-items:center!important;justify-content:center!important}' +
    // Round the email field to match the pill fields used everywhere else in the design.
    'input[type="email"],input[type="text"],input[type="tel"]{border-radius:50px!important}';

  // Inject centering as a persistent <style> the moment the doc exists, before
  // Klaviyo paints its form — so it renders centered with no top->center jump.
  // Only touches <html>/<body>, never a Klaviyo element, so the popup's own
  // canvas layout stays intact. Appended to <html> so a body re-render can't
  // wipe it.
  function center(doc) {
    if (!doc || !doc.documentElement) return;
    if (doc.getElementById('pt-bis-center')) return;
    var style = doc.createElement('style');
    style.id = 'pt-bis-center';
    style.textContent = CSS;
    doc.documentElement.appendChild(style);
  }

  function hook(iframe) {
    if (iframe.__ptBisCentered) return;
    iframe.__ptBisCentered = true;

    function apply() {
      var doc;
      try {
        doc = iframe.contentDocument;
      } catch (e) {
        return;
      }
      if (!doc || !doc.documentElement) return;
      center(doc);
      // Watch <html> (not just <body>) so we inject the moment Klaviyo writes
      // the document — the observer fires before paint, avoiding a top->center
      // jump — and re-inject on any later re-render.
      if (!iframe.__ptBisObserved) {
        iframe.__ptBisObserved = true;
        new MutationObserver(function () {
          center(doc);
        }).observe(doc.documentElement, { childList: true, subtree: true });
      }
    }

    iframe.addEventListener('load', apply);
    apply();
    // Content is written after src=about:blank; poll fast so the style lands
    // before Klaviyo's first paint, then stop.
    var tries = 0;
    var timer = setInterval(function () {
      apply();
      if (++tries > 120) clearInterval(timer);
    }, 16);
  }

  var existing = document.getElementById(IFRAME_ID);
  if (existing) hook(existing);

  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.id === IFRAME_ID) {
          hook(node);
        } else if (node.querySelector) {
          var found = node.querySelector('#' + IFRAME_ID);
          if (found) hook(found);
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* Founder section: reveal the copy line by line instead of letter by letter.

   The base theme splits the paragraph with <split-lines preserve-letters>, then
   animates every CHARACTER individually against a ViewTimeline
   (theme.js startLettersAnimation_fn, ~line 6007): 397 separate animations with
   a 15-character soft edge. It reads as a letter ticker rather than as reading,
   and it is a lot of animations for what the user perceives as one effect.

   <split-lines> already exposes a `.lines` property - the same lines the theme
   groups its characters into - so switching the reveal to whole lines needs no
   re-splitting, just a different animation target. Here that is 10 animations
   instead of 397, each covering ~8.7% of the scroll.

   Done from here rather than by editing theme.js: that file is the compiled
   base-theme bundle and any theme update would overwrite a patch to it. The
   cost is that this runs AFTER the theme has set its own animations up and has
   to cancel them, so it must re-run whenever the theme rebuilds - the theme
   re-splits and re-animates on resize via its own ResizeObserver.

   Note this changes the STYLE of the reveal, not its length. Scroll distance is
   `reading-speed x character count` and stays character-based; use the Reading
   speed setting to shorten it. */
(function () {
  if (!window.ViewTimeline) return; // no scroll-driven timelines: leave the theme's fallback alone

  // How many lines are mid-reveal at any moment. This is the whole difference
  // between "reading" and "nothing is happening": with 7 lines over 317vh, each
  // line owns ~45vh of scroll, so a narrow window leaves long stretches where
  // nothing on screen changes. Measured lines actively changing at a given
  // scroll point: 1.5 -> 1-2 lines, 3 -> 5-7 lines.
  const LINES_AHEAD = 3;

  // Floor for the un-revealed state. The section setting (text_opacity) is 10,
  // i.e. 0.1, which was fine for the per-character reveal because the moving
  // leading edge carried the eye. Per line there is no moving edge, so 0.1 just
  // reads as invisible text over the video. 0.35 stays legible while still
  // clearly "not yet read". Raise text_opacity in the customizer to go higher;
  // this only stops it going lower.
  const MIN_START_OPACITY = 0.35;

  function applyLineReveal(root) {
    const splitLines = root.querySelector('split-lines');
    const lines = Array.from(splitLines?.lines || []);
    if (!lines.length) return false;

    const startOpacity = Math.max(
      parseFloat(root.getAttribute('text-start-opacity')) || 0,
      MIN_START_OPACITY
    );
    const step = 100 / (lines.length + LINES_AHEAD);
    const timeline = new ViewTimeline({
      subject: root,
      axis: 'block',
      inset: `${getComputedStyle(root).getPropertyValue('--sticky-area-height')} 0px`,
    });

    lines.forEach((line, index) => {
      // Release the line element back to the theme. It drives the section's
      // entrance/exit effect (showEnterEffect_fn animates these 0 -> 1), so
      // animating it here as well made the whole paragraph flash fully visible
      // on entry before being pulled back down to the start opacity.
      line.getAnimations()
        .filter((animation) => animation.timeline instanceof ViewTimeline)
        .forEach((animation) => animation.cancel());
      line.style.removeProperty('opacity');

      // The reveal lives on the characters - same as the base theme - but every
      // character in a line shares that line's range, so it reads line by line.
      // Keeping it on the characters matters: they are inside a shadow root and
      // styled from outside via `.reading-text__inner ::part(letter)`
      // (theme.css:8371), which a plain inline style cannot beat. An animation
      // can, which is how the theme's own reveal works.
      const rangeStart = `contain ${index * step}%`;
      const rangeEnd = `contain ${(index + LINES_AHEAD + 1) * step}%`;

      Array.from(line.children).forEach((char) => {
        char.getAnimations().forEach((animation) => animation.cancel());
        char.style.removeProperty('opacity');
        char.animate(
          { opacity: [startOpacity, 1] },
          { fill: 'both', timeline, rangeStart, rangeEnd }
        );
      });
    });
    return true;
  }

  function watch(root) {
    let queued = false;
    const reapply = () => {
      if (queued) return;
      queued = true;
      // Two frames: let the theme finish its own setup pass before overriding it.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          queued = false;
          applyLineReveal(root);
        })
      );
    };

    // The theme re-splits and re-animates on resize; follow the same trigger.
    if ('ResizeObserver' in window) new ResizeObserver(reapply).observe(root);
    // And catch the initial split, which happens when the section scrolls into view.
    const splitLines = root.querySelector('split-lines');
    if (splitLines) {
      new MutationObserver(reapply).observe(splitLines, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    reapply();
  }

  const init = () => document.querySelectorAll('reading-text').forEach(watch);
  if (window.customElements.get('reading-text')) init();
  else window.customElements.whenDefined('reading-text').then(init);
})();

/**
 * Drag-release healer for [allow-drag] carousels
 * ---------------------------------------------------------------------------
 * The theme's ScrollCarousel drag (theme.js) attaches a mousemove scroller on
 * mousedown and removes it only on a document mouseup. If that mouseup is
 * never delivered - the button is released over an iframe (the Tolstoy embeds
 * swallow it), outside the browser window, or over a cross-origin widget - the
 * carousel is left permanently following the bare pointer: hovering across it
 * scrolls it with no button held.
 *
 * Reproduced on the blog carousel: one mousedown with the mouseup suppressed,
 * and every subsequent buttons:0 mousemove dragged scrollLeft around.
 *
 * The theme's onMouseMove never inspects event.buttons, so it cannot notice
 * the release on its own. This healer watches at the document level: when a
 * drag is armed and a mousemove arrives with no buttons pressed, it dispatches
 * the mouseup the theme missed, letting the theme's own cleanup run - listener
 * removal, snap restore, settle scroll. Capture phase, so the heal happens
 * before the carousel's own mousemove handler sees the event: the first bare
 * move ends the drag rather than scrolling once more.
 *
 * Document-level on purpose: every [allow-drag] carousel on the site shares
 * the same defect, so they all share the heal.
 */
(() => {
  let dragArmed = false;

  document.addEventListener(
    'mousedown',
    (event) => {
      if (event.target instanceof Element && event.target.closest('[allow-drag]')) {
        dragArmed = true;
      }
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'mouseup',
    () => {
      dragArmed = false;
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'mousemove',
    (event) => {
      if (!dragArmed || event.buttons !== 0) return;
      dragArmed = false;
      document.dispatchEvent(
        new MouseEvent('mouseup', {
          bubbles: true,
          clientX: event.clientX,
          clientY: event.clientY,
        })
      );
    },
    { capture: true }
  );
})();
/* ------------------------------------------------------------------------------------------------
 * HERO STICKY SCALE + FADE
 * ------------------------------------------------------------------------------------------------
 * The hero pins while the section below it scales 0.9 -> 1, glide-snaps into place and fades the hero
 * out behind it. Shared by `slideshow` and `blog-banner` — whichever of the two is the first section
 * on the page becomes the host. Paired CSS: the `.sticky-slideshow*` rules in custom.css.
 *
 * Gated on the theme setting Animation > "Enable sticky hero animation", which layout/theme.liquid
 * surfaces as `body.features--sticky-hero`.
 * ---------------------------------------------------------------------------------------------- */
{
  const HOST_SELECTOR = '.shopify-section--slideshow, .shopify-section--blog-banner';
  const SCALE_FROM = 0.9;
  const SMOOTHING = 7; // higher = snappier follow, lower = longer glide
  const SNAP_MS = 1200; // scroll glide duration
  const DOWN_TRIGGER = 0.85; // * vh
  const UP_TRIGGER = 0.15; // * vh — higher = less up-scroll needed to return to the hero
  const P_STEPS = 20; // quantization of --sticky-next-p, which drives real layout in CSS
  const SCROLL_IDLE_MS = 150; // quiet period before the scrollbar/keyboard fallback snaps
  /* Every consumer of --sticky-next-p (featured-collections, text-with-media, beauty-brings) sits in a
     min-width:1000px block, so below that the write is a style invalidation nobody reads. */
  const P_MEDIA = '(min-width: 1000px)';
  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
  const clamp01 = (value) => Math.max(0, Math.min(1, value));

  // Speed ramp from the Figma prototype: cubic-bezier(0.45, 0.05, 0.15, 1), solved like CSS does.
  const snapEase = ((x1, y1, x2, y2) => {
    const bez = (t, a, b) => 3 * t * (1 - t) * (1 - t) * a + 3 * t * t * (1 - t) * b + t * t * t;
    return (x) => {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      let lo = 0;
      let hi = 1;
      let t = x;
      for (let i = 0; i < 24; i++) {
        const cx = bez(t, x1, x2);
        if (Math.abs(cx - x) < 0.0005) break;
        if (cx < x) lo = t;
        else hi = t;
        t = (lo + hi) / 2;
      }
      return bez(t, y1, y2);
    };
  })(0.45, 0.05, 0.15, 1);

  /* A hidden section still emits an empty wrapper — child count is the reliable "did Liquid output
     anything" signal, since a real section's height can read 0 before its media loads. */
  const isRenderedSection = (element) =>
    element.classList.contains('shopify-section') && element.childElementCount > 0;

  /* First section in `parent` that actually put something on the page. */
  function firstRenderedSection(parent) {
    let element = parent.firstElementChild;
    while (element && !isRenderedSection(element)) element = element.nextElementSibling;
    return element;
  }

  /* The animation owns the top of the page, so the host must be the topmost section in its parent —
     first *rendered* one, not first child. A section that renders no output still emits its wrapper
     (a hero-less collection suppresses `slideshow` this way), and matching that wrapper would hand
     back an empty section instead of the hero. Sections disabled in the editor emit nothing at all,
     so the `main-page` ahead of the hero on page.about and friends never reaches the DOM. */
  function findHost() {
    for (const candidate of document.querySelectorAll(HOST_SELECTOR)) {
      const parent = candidate.parentElement;
      if (!parent || firstRenderedSection(parent) !== candidate) continue;
      // A slideshow section without a carousel is a plain banner — nothing to pin.
      if (
        candidate.classList.contains('shopify-section--slideshow') &&
        !candidate.querySelector('slideshow-carousel')
      ) {
        continue;
      }
      /* A blog banner with no image renders `.blog-banner__plain` — copy-height, no hero. The pin
         needs a viewport-tall hero (`next` has to start below the down trigger to have anywhere to
         travel), so that layout stays exactly as it is and the animation sits out. */
      if (candidate.querySelector('.blog-banner__plain')) continue;
      return candidate;
    }
    return null;
  }

  function init(hero, isFirstRun) {
    let next = hero.nextElementSibling;
    while (next && !isRenderedSection(next)) next = next.nextElementSibling;
    if (!next) return null;

    const parent = hero.parentElement;
    const root = document.documentElement;
    const headerSection = document.querySelector('.shopify-section--header');
    const xHeader = document.querySelector('x-header[hide-on-scroll]');
    const pQuery = window.matchMedia(P_MEDIA);
    const controller = new AbortController();
    const listen = (target, type, handler, options) =>
      target.addEventListener(type, handler, { ...options, signal: controller.signal });

    let userEngaged = false;
    let nextIsPinned = false; // position:sticky|fixed on `next`
    let headerIsPinned = false;
    let nextRectTop = 0; // viewport-relative top of `next`
    let nextDocTop = 0; // document Y of next's *flow* top
    let headerOffset = 0; // bottom edge of the pinned header
    let vh = window.innerHeight; // re-read on resize only; innerHeight is stable between them
    let curScale = 1;
    let curOpacity = 1;
    let targetScale = 1;
    let targetOpacity = 1;
    let fading = false;
    let live = false; // compositor hints are on
    let lastScale = -1;
    let lastOpacity = -1;
    let lastP = -1;
    let rafId = null;
    let lastT = 0;
    let armedDown = true;
    let armedUp = true;
    let snapping = false;
    let snapFrom = 0;
    let snapDist = 0;
    let snapStart = 0;
    let snapDone = null;
    let commandedY = window.pageYOffset; // where our tween wants the page right now
    let lastScrollY = window.pageYOffset;
    let lastScrollAt = 0;
    let pendingDir = 0; // direction the fallback snap should test once scrolling goes quiet
    let holdHeader = false;
    let sentinel = null;
    let resizeObserver = null;
    let touchY = null;

    hero.classList.add('sticky-slideshow');
    next.classList.add('sticky-slideshow__next');

    /* Any genuine input means the visitor is driving the page and the `load` reset below must not
       override it — keydown/pointerdown cover the paths that fire no wheel event at all. */
    const markEngaged = () => {
      userEngaged = true;
    };
    listen(window, 'keydown', markEngaged, { passive: true, capture: true });
    listen(window, 'pointerdown', markEngaged, { passive: true, capture: true });

    if (isFirstRun) {
      // A refresh must always start at scroll 0 — kill the browser's restored scroll position.
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      window.scrollTo(0, 0);
      /* Backstop for browsers that restore scroll after `load` regardless. It must be gated: `load`
         on a storefront home page lands seconds in, and re-zeroing there yanks the page out from
         under a gesture (and reads as >120px drift to clampScroll, killing a mid-glide snap). */
      listen(window, 'load', () => {
        if (userEngaged || snapping) return;
        if (window.pageYOffset !== 0) window.scrollTo(0, 0);
      });
    }

    // Neither `position` nor the viewport animates here, so resolve them on layout changes only.
    function readStyles() {
      vh = window.innerHeight;
      const pos = getComputedStyle(next).position;
      nextIsPinned = pos === 'sticky' || pos === 'fixed';
      if (headerSection) {
        const headerPos = getComputedStyle(headerSection).position;
        headerIsPinned = headerPos === 'sticky' || headerPos === 'fixed';
      }
    }

    /* Rests the down-snap below the pinned header, less 50px so the section tucks slightly under it
       instead of leaving a gap. Only the header hold and a starting snap consume it, and hide-on-scroll
       moves the header under us — so read it at those two points instead of once per frame. */
    function measureHeaderOffset() {
      headerOffset =
        headerSection && headerIsPinned ? Math.max(0, headerSection.getBoundingClientRect().bottom - 50) : 0;
    }

    /* One layout read per frame, cached: trySnap() runs from the non-passive wheel/touchmove handlers
       and a getBoundingClientRect there stalls the browser's scroll on every tick. nextDocTop is next's
       *flow* top — a position:sticky `next` (about page) pins to the viewport, so its rect.top reads 0
       at the page bottom and any up-scroll there looked like "at the hero". */
    function measure(y) {
      nextRectTop = next.getBoundingClientRect().top;
      nextDocTop =
        !nextIsPinned || nextRectTop > 0
          ? y + nextRectTop
          : y + parent.getBoundingClientRect().top + hero.offsetHeight;
      if (holdHeader) measureHeaderOffset(); // releaseIfPastHero needs it live until the hold ends
    }

    /* Scale runs while next's top travels from 85% to 20% of vh; the hero fades from 50% to 20%.
       Writes the pair into closure state rather than returning it — this runs every frame. */
    function readTargets() {
      const top = nextRectTop;
      const scaleP = clamp01((vh * 0.85 - top) / (vh * 0.65));
      const fadeP = clamp01((vh * 0.5 - top) / (vh * 0.3));
      targetScale = SCALE_FROM + (1 - SCALE_FROM) * scaleP;
      targetOpacity = 1 - fadeP;
    }

    /* will-change keeps a full-viewport layer alive for each element, so it is worth having only while
       the animation can still move. Once the hero has faded out completely it is over until the visitor
       scrolls back up, and that transition is rare enough that re-promoting there costs nothing. */
    function setLive(active) {
      if (active === live) return;
      live = active;
      hero.classList.toggle('is-sticky-live', active);
      next.classList.toggle('is-sticky-live', active);
    }

    /* Transform and opacity writes are composited, but --sticky-next-p feeds gap/padding rules in
       custom.css and inherits into next's whole subtree — writing it per frame relayouts 60x/s, which
       the next frame's measure() then has to flush. Quantized to P_STEPS the widest 4rem span jumps
       by ~3px (invisible mid-scale) and layout runs ~20 times per glide instead of ~72. */
    function apply() {
      // Quantize first and compare numbers: an unchanged frame then costs no string at all.
      const scale = Math.round(curScale * 1e4) / 1e4;
      if (scale !== lastScale) {
        lastScale = scale;
        next.style.transform = `translate3d(0,0,0) scale(${scale})`;
      }
      // 0 at the initial scaled-down state, 1 at full view — CSS expands spacings in lockstep.
      if (pQuery.matches) {
        const p = Math.round(((curScale - SCALE_FROM) / (1 - SCALE_FROM)) * P_STEPS) / P_STEPS;
        if (p !== lastP) {
          lastP = p;
          next.style.setProperty('--sticky-next-p', String(p));
        }
      }
      const opacity = Math.round(curOpacity * 1e3) / 1e3;
      if (opacity !== lastOpacity) {
        lastOpacity = opacity;
        hero.style.opacity = String(opacity);
      }
      const isFading = curOpacity < 0.999;
      if (isFading !== fading) {
        fading = isFading;
        hero.classList.toggle('is-fading', isFading);
      }
      // Fully faded at full size = nothing left to animate until the visitor comes back up.
      setLive(!(opacity === 0 && scale === 1));
    }

    /* One loop for everything: our own scroll tween, the exponential lerp toward the scroll-derived
       target, and the idle check that stands in for inputs which fire no wheel/touch event. The tween
       used to own a second requestAnimationFrame chain, which meant a snap ran two loops that each
       measured and wrote — and the scale lagged the scroll it was reacting to by a frame. Driving the
       scroll first and measuring after keeps them on the same frame. */
    function frame(now) {
      const dt = lastT ? Math.min((now - lastT) / 1000, 0.05) : 1 / 60;
      lastT = now;

      if (snapping) {
        if (!snapStart) snapStart = now;
        const progress = Math.min((now - snapStart) / SNAP_MS, 1);
        commandedY = snapFrom + snapDist * snapEase(progress);
        window.scrollTo(0, commandedY);
        if (progress >= 1) {
          snapping = false;
          lastScrollY = commandedY;
          const done = snapDone;
          snapDone = null;
          if (done) done();
        }
      }

      measure(window.pageYOffset); // the frame's single layout read, before any write
      readTargets();
      const k = 1 - Math.exp(-SMOOTHING * dt);
      curScale += (targetScale - curScale) * k;
      curOpacity += (targetOpacity - curOpacity) * k;
      const atRest =
        Math.abs(targetScale - curScale) < 0.0005 && Math.abs(targetOpacity - curOpacity) < 0.002;
      if (atRest) {
        curScale = targetScale;
        curOpacity = targetOpacity;
      }
      apply();

      /* Scrollbar drag / keyboard paging emit no wheel or touch event, so the snap has to wait for the
         scroll position to go quiet — snapping mid-drag has the held thumb and the tween both setting
         positions every frame, which flickers. */
      if (!snapping && pendingDir && now - lastScrollAt >= SCROLL_IDLE_MS) {
        const dir = pendingDir;
        pendingDir = 0;
        trySnap(dir);
      }

      if (snapping || pendingDir || !atRest) {
        rafId = requestAnimationFrame(frame);
      } else {
        rafId = null; // converged: park the loop, scroll/resize wakes it
        lastT = 0;
      }
    }

    function wake() {
      if (rafId === null) rafId = requestAnimationFrame(frame);
    }

    /* theme.css gates the transparent header on two :has() with :root as the subject; mirroring that
       in CSS re-ran document-wide invalidation on nearly every DOM mutation, and the hold is active
       exactly while the page hydrates. Both conditions are static for the page — resolve them once. */
    const wantsTransparentHeader =
      !!hero.querySelector('[allow-transparent-header]') &&
      !document.querySelector('.shopify-section--header ~ .shopify-section-group-header-group');

    /* Keep the header visible until the intro snap has played, then hand hide-on-scroll back to the
       theme untouched. Never do this by stripping `.is-hidden` off x-header: its setVisibility()
       no-ops when the requested state already matches, so its private field stays stuck on "hidden"
       and the header never hides again for the session. Suppress the hide at the source instead —
       x-header already refuses to hide while it contains an `[open]` element, and bails before
       touching that state. The sentinel sits inside a wrapper so no sibling selector can reach it;
       `:has([open])` on `.header` still matches, hence the transparent re-assert in custom.css. */
    function holdHeaderVisible() {
      if (holdHeader) return;
      holdHeader = true;
      root.classList.add('sticky-header-hold');
      if (wantsTransparentHeader) root.classList.add('sticky-header-hold--transparent');
      if (xHeader && !sentinel) {
        sentinel = document.createElement('span');
        sentinel.hidden = true;
        sentinel.setAttribute('data-sticky-header-hold', '');
        const flag = document.createElement('span');
        flag.setAttribute('open', '');
        sentinel.appendChild(flag);
        xHeader.appendChild(sentinel);
      }
    }

    function releaseHeader() {
      if (!holdHeader) return;
      holdHeader = false;
      root.classList.remove('sticky-header-hold', 'sticky-header-hold--transparent');
      if (sentinel) {
        sentinel.remove();
        sentinel = null;
      }
    }

    holdHeaderVisible();

    /* Trackpad inertia keeps firing scroll events after the fingers lift and preventDefault can't stop
       that momentum, so clamp small drift back to the tween's commanded Y. A big deviation means the
       user grabbed the scrollbar — fighting that flickers, so yield and let them drive. */
    function clampScroll(y) {
      if (!snapping) return;
      const drift = Math.abs(y - commandedY);
      if (drift > 120) {
        snapping = false; // scrollbar takeover: abort the glide, no fight
        snapDone = null;
        lastScrollY = y;
        releaseHeader(); // the glide's onDone never runs on an abort
      } else if (drift > 0.5) {
        window.scrollTo(0, commandedY);
      }
    }

    // Hand the scroll to the frame loop for exactly SNAP_MS, easing fromY -> targetY.
    function startSnap(fromY, targetY, onDone) {
      const dist = targetY - fromY;
      if (Math.abs(dist) < 1) {
        window.scrollTo(0, targetY);
        snapping = false;
        commandedY = targetY;
        lastScrollY = targetY;
        if (onDone) onDone();
        return;
      }
      snapping = true;
      snapFrom = fromY;
      snapDist = dist;
      snapStart = 0;
      snapDone = onDone || null;
      commandedY = fromY;
      pendingDir = 0; // the glide supersedes any fallback the idle check was about to run
      window.scrollTo(0, fromY);
      wake();
    }

    /* A modal/drawer (Klaviyo popup, cart drawer...) locks the page with overflow:hidden — that stops
       user scrolling but not window.scrollTo, so without this the snap runs behind the modal. */
    function scrollLocked() {
      return getComputedStyle(document.body).overflowY === 'hidden' || getComputedStyle(root).overflowY === 'hidden';
    }

    /* The down snap is the normal way the hold ends, but it can be skipped entirely (a modal owned the
       page at the crossing, an anchor or keyboard jump landed past the hero, the page opened
       mid-scroll) — release however we got there, or the hold latches on for the rest of the page. */
    function releaseIfPastHero(y) {
      if (!holdHeader || snapping) return;
      if (nextDocTop - y <= headerOffset + 1) releaseHeader();
    }

    /* Hijack an input in direction `dir` (1 down, -1 up) carrying `delta` px of not-yet-applied scroll;
       returns true when it consumed the gesture. Page scrolling is composited, so by the time a scroll
       event runs the overshot frame is already on screen and rewinding always shows a jump — acting on
       the wheel/touch delta instead means the overshoot never exists. Reads only the frame cache;
       scrollLocked() is the one unavoidable getComputedStyle, deferred to an actual snap. */
    function trySnap(dir, delta) {
      if (snapping) return true; // already animating: block the input
      const y = window.pageYOffset;
      const top = nextDocTop - y; // viewport-relative flow top of next
      const predicted = y + (delta || 0);

      // next not at the top yet, and this gesture would cross the down line
      if (dir > 0 && armedDown && top > 0 && predicted >= nextDocTop - vh * DOWN_TRIGGER) {
        if (scrollLocked()) return false; // modal owns the page: don't hijack
        armedDown = false;
        armedUp = true;
        measureHeaderOffset(); // hide-on-scroll may have moved the header since the hold ended
        startSnap(y, Math.max(0, nextDocTop - headerOffset), releaseHeader);
        return true;
      }
      // next at/near the top, and this gesture would cross the up line
      if (dir < 0 && armedUp && top < vh && predicted <= nextDocTop - vh * UP_TRIGGER) {
        if (scrollLocked()) return false;
        armedUp = false;
        armedDown = true;
        startSnap(y, 0, holdHeaderVisible);
        return true;
      }
      return false;
    }

    /* Public hook for the slide CTA fallback (snippets/slide-cta-scroll-next): a plain scrollIntoView
       would be clamped mid-glide, so the CTA hands the gesture here and reuses this exact snap.
       Returns false when there is nothing to travel, so the caller can fall back. */
    hero.__stickySnapToNext = function () {
      if (snapping) return true; // a glide already owns the page
      if (scrollLocked()) return false;
      // Cold path (a click) and the rAF loop may have been parked a while — measure fresh.
      readStyles();
      const y = window.pageYOffset;
      measure(y);
      measureHeaderOffset();
      const targetY = Math.max(0, nextDocTop - headerOffset);
      if (Math.abs(targetY - y) < 1) return false;
      armedDown = false;
      armedUp = true;
      startSnap(y, targetY, releaseHeader);
      return true;
    };

    listen(
      window,
      'wheel',
      (event) => {
        userEngaged = true;
        // deltaMode 1 = lines (Firefox), 2 = pages — normalize to px
        const unit = event.deltaMode === 1 ? 40 : event.deltaMode === 2 ? window.innerHeight : 1;
        const delta = event.deltaY * unit;
        const dir = delta > 0 ? 1 : delta < 0 ? -1 : 0;
        if (dir && trySnap(dir, delta)) event.preventDefault();
      },
      { passive: false }
    );

    listen(
      window,
      'touchstart',
      (event) => {
        userEngaged = true;
        touchY = event.touches[0].clientY;
      },
      { passive: true }
    );

    listen(
      window,
      'touchmove',
      (event) => {
        if (touchY === null) return;
        const dy = touchY - event.touches[0].clientY; // finger up = scroll down
        const dir = dy > 0 ? 1 : dy < 0 ? -1 : 0;
        if (dir && trySnap(dir, dy)) {
          event.preventDefault();
          return;
        }
        touchY = event.touches[0].clientY;
      },
      { passive: false }
    );

    /* Feeds the loop, and doubles as the scrollbar-drag / keyboard path: those emit no wheel event, so
       the direction is banked here and the loop snaps once scrolling goes quiet (see frame()).
       nextDocTop is scroll-invariant, so once the hero is a full viewport behind us there is nothing
       left to measure or animate until the visitor heads back toward it — the cheapest frame is the
       one we never schedule, and on a long page that is every frame of the rest of the scroll. */
    listen(
      window,
      'scroll',
      () => {
        const y = window.pageYOffset;
        if (snapping) {
          clampScroll(y); // hold the line vs trackpad momentum
          wake();
          return;
        }
        const dir = y > lastScrollY ? 1 : y < lastScrollY ? -1 : 0;
        lastScrollY = y;
        releaseIfPastHero(y);
        if (y > nextDocTop + vh) return;
        if (dir) {
          pendingDir = dir;
          lastScrollAt = performance.now();
        }
        wake();
      },
      { passive: true }
    );

    // Land on the correct state at init so a page opened mid-scroll never animates into place.
    readStyles();
    measureHeaderOffset();
    measure(window.pageYOffset);
    readTargets();
    curScale = targetScale;
    curOpacity = targetOpacity;
    apply();
    if (nextRectTop < vh * DOWN_TRIGGER) {
      armedDown = false;
      releaseIfPastHero(window.pageYOffset); // opened past the hero: never hold in the first place
    }

    function remeasure() {
      readStyles();
      measure(window.pageYOffset);
      wake();
    }

    listen(window, 'resize', remeasure, { passive: true });
    listen(window, 'load', remeasure);

    /* Crossing the breakpoint changes whether --sticky-next-p has a consumer at all: drop the property
       on the way down so nothing stale is inherited, and force a rewrite on the way up. */
    listen(pQuery, 'change', () => {
      lastP = -1;
      if (!pQuery.matches) next.style.removeProperty('--sticky-next-p');
      wake();
    });

    /* The hero settles late — LCP image decode, autoplay video metadata, font swap — and until it does
       everything below it sits at the wrong Y. Observe the hero only: next's own height is driven by
       --sticky-next-p, so observing it would feed our writes back in as a resize loop. */
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(remeasure);
      resizeObserver.observe(hero);
    }

    return function destroy() {
      controller.abort();
      if (resizeObserver) resizeObserver.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
      snapping = false;
      snapDone = null;
      pendingDir = 0;
      releaseHeader();
      delete hero.__stickySnapToNext;
      hero.classList.remove('sticky-slideshow', 'is-fading', 'is-sticky-live');
      hero.style.opacity = '';
      next.classList.remove('sticky-slideshow__next', 'is-sticky-live');
      next.style.transform = '';
      next.style.removeProperty('--sticky-next-p');
    };
  }

  const motionQuery = window.matchMedia(REDUCED_MOTION);
  let teardown = null;
  let isFirstRun = true;
  let hostObserver = null;
  let rebindQueued = false;

  /* The Instant Shortcode Builder app embed runs `main.innerHTML = main.innerHTML.replace(...)` to swap
     its [shortcode] tokens (assets/setShortCode.js). Deferred app-embed scripts run after this module,
     so that reparses every section *after* we have bound: the classes and inline styles survive inside
     the serialized HTML, but the nodes are new ones. The closure then measures and writes elements that
     are no longer in the document, which looks exactly like a dead animation — pinned hero, `next`
     frozen at scale(0.9), nothing responding to scroll. This is why the about page (a page template,
     where the app targets `main`) stopped animating once the code moved out of the section and into a
     module in the head; the old per-section script ran late enough to miss the rewrite.

     So don't assume the host outlives us: watch its parent and rebind the moment it leaves the DOM. */
  function watchHost(hero) {
    const parent = hero.parentElement;
    if (!parent || !('MutationObserver' in window)) return;
    hostObserver = new MutationObserver(() => {
      if (hero.isConnected || rebindQueued) return;
      rebindQueued = true;
      // A wholesale innerHTML write lands in one task — rebind after it, not part-way through.
      setTimeout(() => {
        rebindQueued = false;
        boot();
      }, 0);
    });
    hostObserver.observe(parent, { childList: true });
  }

  /* Re-runs on theme-editor section load/unload and after a host swap — the host, or the section below
     it, may have changed, so tear the previous instance down (listeners, classes, inline styles) first. */
  function boot() {
    if (teardown) {
      teardown();
      teardown = null;
    }
    if (hostObserver) {
      hostObserver.disconnect();
      hostObserver = null;
    }
    if (!document.body.classList.contains('features--sticky-hero')) return;
    if (motionQuery.matches) return;
    const hero = findHost();
    if (!hero) return;
    teardown = init(hero, isFirstRun);
    isFirstRun = false;
    watchHost(hero);
  }

  boot();

  // Toggling "reduce motion" mid-session should take effect without a reload, both ways.
  motionQuery.addEventListener('change', boot);

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', boot);
    document.addEventListener('shopify:section:unload', boot);
  }
}

/* ============================================================
   Subtle image fade-in on first viewport entry
   ------------------------------------------------------------
   Replicates the collections-carousel promo image entrance (fade +
   slight rise) for other large section images. Scope is decided by
   measurement, not by selector: any <img> at least 300x300 rendered
   inside a section, excluding surfaces that already run their own
   entrance (product cards, the slideshow hero, Tolstoy, the PDP
   gallery). The featured-collections promo image is INCLUDED: the theme
   does not animate it on entry, so this fade IS its entrance.

   Mechanism: a small poll (not IntersectionObserver - an earlier IO
   version proved fragile here, and its blanket timed failsafe played
   the animation off-screen, so nothing visibly faded). Every 400ms
   any pending image whose box has entered the viewport plays the
   entrance; the poll stops when none remain.

   SAFETY MODEL (the reveal-feature rule): an image is hidden ONLY by
   the same code path that reveals it, and only when it is still below
   the viewport at arm time - above-fold content is never touched. The
   reveal check is plain geometry each tick, so there is no observer
   to die; any error during arming reveals everything immediately.
   ============================================================ */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  /* Theme setting toggle (Theme settings > Animation > "Fade in section
     images on scroll"). Absent themeVariables (unlikely) means enabled. */
  var tv = window.themeVariables;
  if (tv && tv.settings && tv.settings.imageFadeIn === false) return;

  var PENDING = 'cimg-fade-pending';
  var PLAY = 'cimg-fade-in';

  function revealAll() {
    document.querySelectorAll('.' + PENDING).forEach(function (el) {
      el.classList.remove(PENDING);
      el.classList.add(PLAY);
    });
  }

  function arm() {
    try {
      var excluded = 'product-card, .slideshow, tolstoy-carousel, .product-gallery, [class*="tolstoy"], [reveal-on-scroll]';
      var pending = [];
      document.querySelectorAll('.shopify-section img').forEach(function (img) {
        if (img.closest(excluded)) return;
        var r = img.getBoundingClientRect();
        if (r.width < 300 || r.height < 300) return;
        if (r.top < window.innerHeight) return; /* above or in the fold: never touch */
        /* Sections with a color overlay render it on a wrapper ABOVE the
           image - hiding only the img would show the overlay as a bare grey
           box until the image fades in under it. Arm the overlay wrapper in
           that case so image and overlay enter together. */
        var target = img.closest('.with-overlay') || img;
        if (target.classList.contains(PENDING)) return;
        target.classList.add(PENDING);
        pending.push({ el: target, since: 0 });
      });

      if (!pending.length) return;

      /* All imgs inside the target fetched and decodable. Lazy images only
         start fetching once their box nears the viewport, so this naturally
         becomes true shortly after the target scrolls in. */
      function ready(el) {
        var imgs = el.tagName === 'IMG' ? [el] : el.querySelectorAll('img');
        for (var i = 0; i < imgs.length; i++) {
          if (!(imgs[i].complete && imgs[i].naturalWidth > 0)) return false;
        }
        return true;
      }

      var poll = setInterval(function () {
        try {
          var now = Date.now();
          pending = pending.filter(function (entry) {
            var r = entry.el.getBoundingClientRect();
            /* 90% of the viewport, mirroring the reference's early trigger */
            var inView = r.top < window.innerHeight * 0.9 && r.bottom > 0;
            if (!inView) { entry.since = 0; return true; }
            if (!entry.since) entry.since = now;
            /* Fade only once the image has LOADED - otherwise the entrance
               plays on an empty box and the image pops in afterwards. The
               2.5s cap keeps a slow or broken image from staying hidden:
               past it the target is revealed regardless and the image
               appears natively whenever it arrives. */
            if (!ready(entry.el) && now - entry.since < 2500) return true;
            entry.el.classList.remove(PENDING);
            entry.el.classList.add(PLAY);
            return false;
          });
          if (!pending.length) clearInterval(poll);
        } catch (e) {
          clearInterval(poll);
          revealAll();
        }
      }, 200);
    } catch (e) {
      revealAll();
    }
  }

  /* Arm only after the full load event: at DOMContentLoaded images have not
     laid out yet, so everything measures near the page top and the above-fold
     guard wrongly skips it (and sizes read 0 for unloaded media). */
  if (document.readyState === 'complete') {
    arm();
  } else {
    window.addEventListener('load', arm, { once: true });
  }
})();

/* ============================================================
   Reviews anchor: actually land on the widget (BugHerd #434)
   ------------------------------------------------------------
   Rating badges link to #yotpo-reviews-main-widget, but the widget is
   client-side rendered, so the native anchor jump fires before the
   target exists and nothing scrolls. Handle both entry paths:
   - arriving on a page with that hash
   - clicking an in-page link with that hash
   Poll briefly for the widget, then smooth-scroll. Gives up silently
   after 8s (widget genuinely absent, e.g. reviews app off).
   ============================================================ */
(function () {
  var HASH = '#yotpo-reviews-main-widget';

  function scrollToReviews() {
    var tries = 0;
    var timer = setInterval(function () {
      var el = document.getElementById('yotpo-reviews-main-widget');
      if (el && el.offsetHeight > 0) {
        clearInterval(timer);
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (++tries > 40) {
        clearInterval(timer);
      }
    }, 200);
  }

  if (window.location.hash === HASH) {
    if (document.readyState === 'complete') scrollToReviews();
    else window.addEventListener('load', scrollToReviews, { once: true });
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[href*="' + HASH + '"]');
    if (!link) return;
    var url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname) return; /* cross-page: let it navigate */
    scrollToReviews();
  });
})();

/* ============================================================
   Rewards page: dead hero buttons (BugHerd #442)
   ------------------------------------------------------------
   The Yotpo loyalty hero renders its Sign up / LOG IN buttons as
   anchors with href="#" (widget config gap). Rewrite them to the real
   account routes once the widget mounts. Text-matched so a properly
   configured button is left alone.
   ============================================================ */
(function () {
  if (!/\/pages\/major-rewards-program/.test(window.location.pathname)) return;

  var MAP = [
    { re: /sign\s*up/i, href: '/account/register' },
    { re: /log\s*in/i, href: '/account/login' }
  ];

  var tries = 0;
  var timer = setInterval(function () {
    var dead = document.querySelectorAll('a[href="#"]');
    dead.forEach(function (a) {
      var text = a.textContent.trim();
      MAP.forEach(function (m) {
        if (m.re.test(text)) a.setAttribute('href', m.href);
      });
    });
    if (++tries > 30) clearInterval(timer); /* widget settles well within 15s */
  }, 500);
})();

/* ============================================================
   Rewards page: reveal the loyalty widgets once hydrated (#467)
   ------------------------------------------------------------
   custom.css starts .yotpo-widget-loyalty-page transparent; this adds
   .ldy-ready once the widget containers have real rendered height, so
   the shopper never sees the overlapping half-built state. Failsafe
   reveal after 5s no matter what (reveal-feature rule: hidden only by
   the code that reveals, plus a timer that cannot be dodged).
   ============================================================ */
(function () {
  if (!document.body || !document.body.classList.contains('template-page-rewards')) return;

  // Yotpo builds .yotpo-widget-loyalty-page client-side, so it is absent on first tick; poll for the wrapper itself instead of bailing when it is missing.
  var start = Date.now();
  var timer = setInterval(function () {
    var page = document.querySelector('.yotpo-widget-loyalty-page');
    var elapsed = Date.now() - start;

    if (page) {
      var hydrated = [].slice.call(page.querySelectorAll('.yotpo-widget-instance-container'))
        .filter(function (el) { return el.offsetHeight > 100; }).length >= 2;
      if (hydrated || elapsed > 5000) {
        clearInterval(timer);
        page.classList.add('ldy-ready');
      }
    } else if (elapsed > 20000) {
      clearInterval(timer); /* Yotpo never rendered; the CSS failsafe reveals whatever appears later */
    }
  }, 200);
})();

/* Cart line quantity: the +/- controls are static /cart/change links carrying the quantity baked in
   at render time, so every tap before the drawer re-renders (~1s) asks for the same quantity and the
   count only moves by one step. Track the intended quantity locally, show it instantly, and send a
   single request once the tapping stops. */
(function () {
  if (window.__ptCartQtyInit) return;
  window.__ptCartQtyInit = true;

  var FLUSH_DELAY = 400;
  var pending = {};

  function lineInput(key) {
    return document.querySelector('input[data-line-key="' + key.replace(/"/g, '\\"') + '"]');
  }

  function cancel(key) {
    if (!pending[key]) return;
    clearTimeout(pending[key].timer);
    delete pending[key];
  }

  // Hand the final quantity back to the theme's own line-item handler, which owns the request and the re-render.
  function flush(key) {
    var entry = pending[key];
    if (!entry) return;
    delete pending[key];

    var input = lineInput(key);
    if (!input) return;

    input.value = entry.quantity;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  document.addEventListener(
    'click',
    function (event) {
      var link = event.target.closest && event.target.closest('.quantity-selector--line-item a.quantity-selector__button');
      if (!link) return;

      var url = new URL(link.href, window.location.origin);
      var key = url.searchParams.get('id');
      var requested = parseInt(url.searchParams.get('quantity'), 10);
      var input = key ? lineInput(key) : null;
      if (!key || isNaN(requested) || !input) return;

      event.preventDefault();
      event.stopPropagation();

      // defaultValue stays at the server-rendered quantity, so the link's own step survives our optimistic updates.
      var step = requested - parseInt(input.defaultValue, 10);
      if (!step) return;

      var min = parseInt(input.min, 10);
      var max = input.hasAttribute('max') ? parseInt(input.max, 10) : Infinity;
      var next = (pending[key] ? pending[key].quantity : parseInt(input.value, 10)) + step;

      /* Minus at quantity 1 asks for 0 = remove the line - the native
         behavior of the anchor this handler intercepts (cart/change with
         quantity 0 deletes the item). The clamp below floored it back to 1,
         which is why the minus button could never remove a product. Send
         the removal immediately (no debounce - there is nothing further to
         accumulate) and dim the row like the remove-button path does. */
      if (next <= 0) {
        cancel(key);
        var lineItem = input.closest('line-item');
        if (lineItem) lineItem.classList.add('line-item--busy');
        input.value = 0;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      next = Math.max(isNaN(min) ? 1 : min, Math.min(max, next));

      input.value = next;
      pending[key] = { quantity: next, timer: setTimeout(function () { flush(key); }, FLUSH_DELAY) };
    },
    true
  );

  // Removing supersedes any queued quantity change, and the row dims right away instead of after the round-trip.
  document.addEventListener(
    'click',
    function (event) {
      var remove = event.target.closest && event.target.closest('.line-item__remove-button');
      if (!remove) return;

      var lineItem = remove.closest('line-item');
      if (!lineItem) return;

      var input = lineItem.querySelector('input[data-line-key]');
      if (input) cancel(input.getAttribute('data-line-key'));
      lineItem.classList.add('line-item--busy');
    },
    true
  );

  // A rejected change never re-renders the drawer, so release the dimmed row once the request ends either way.
  document.addEventListener('theme:loading:end', function () {
    setTimeout(function () {
      document.querySelectorAll('.line-item--busy').forEach(function (lineItem) {
        lineItem.classList.remove('line-item--busy');
      });
    }, 300);
  });
})();

/* Sorting or removing a filter from page 3 left the shopper on page 3: facet-link[merge-params] seeds the
   new URL from the current one, so "page" survives. Reset to the first page unless the link itself paginates. */
(function () {
  if (window.__ptFacetPageResetInit) return;
  window.__ptFacetPageResetInit = true;

  document.addEventListener(
    'facet:update',
    function (event) {
      var url = event.detail && event.detail.url;
      if (!url || !url.searchParams.has('page')) return;

      // Pagination uses the same element; those links carry their own "page" and must keep it.
      var link = event.target && event.target.tagName === 'FACET-LINK' ? event.target.querySelector('a[href]') : null;
      if (link && new URL(link.href, window.location.origin).searchParams.has('page')) return;

      url.searchParams.delete('page');
    },
    true
  );
})();

/* Cart drawer "Saved for later" tab: the wishlist lives in Swym, so the list is assembled client-side.
   Each row comes from the cart-saved-for-later-row section (Section Rendering API) rather than JS-built
   markup, so prices, swatches and add-to-cart stay on the theme's own line-item components. */
(function () {
  if (window.customElements.get('saved-for-later-list')) return;

  var SECTION_ID = 'cart-saved-for-later-row';

  function handleFromUrl(url) {
    var match = /\/products\/([^/?#]+)/.exec(url || '');
    return match ? match[1] : null;
  }

  window.customElements.define(
    'saved-for-later-list',
    class SavedForLaterList extends HTMLElement {
      connectedCallback() {
        this.rows = this.querySelector('.saved-for-later__rows');
        this.empty = this.querySelector('.saved-for-later__empty');
        this.abortController = new AbortController();
        document.addEventListener('wishlist:change', this.onWishlistChange.bind(this), {
          signal: this.abortController.signal
        });
        this.load();
      }

      disconnectedCallback() {
        this.abortController.abort();
      }

      // An un-hearted row is no longer saved, so it leaves the list at once; a new heart pulls the list again.
      onWishlistChange(event) {
        var detail = event.detail || {};

        if (detail.added) return this.load();

        var button = this.rows.querySelector('.swym-inline-wishlist[data-epi="' + detail.epi + '"]');
        var row = button ? button.closest('line-item') : null;
        if (!row) return;

        row.remove();
        if (!this.rows.querySelector('line-item')) this.showEmpty();
      }

      // Swym boots late and the drawer can render before it exists; SwymCallbacks replays once ready.
      load() {
        var element = this;

        if (!window._swat || typeof window._swat.fetch !== 'function') {
          window.SwymCallbacks = window.SwymCallbacks || [];
          window.SwymCallbacks.push(function () {
            element.load();
          });
          return;
        }

        window._swat.fetch(
          function (items) {
            element.build(items || []);
          },
          function () {
            element.showEmpty();
          }
        );
      }

      build(items) {
        var element = this;
        var rows = [];

        items.forEach(function (item) {
          var handle = handleFromUrl(item && item.du);
          if (handle && item.epi) rows.push({ handle: handle, variant: item.epi });
        });

        if (!rows.length) return this.showEmpty();

        Promise.all(
          rows.map(function (row) {
            var url =
              Shopify.routes.root +
              'products/' +
              row.handle +
              '?variant=' +
              encodeURIComponent(row.variant) +
              '&section_id=' +
              SECTION_ID;

            return fetch(url)
              .then(function (response) {
                return response.ok ? response.text() : '';
              })
              .then(function (text) {
                // Unwrap the section shell: every row shares one section id, so keeping it would repeat the id.
                if (!text) return '';
                var rendered = new DOMParser().parseFromString(text, 'text/html');
                var section = rendered.getElementById('shopify-section-' + SECTION_ID);
                return section ? section.innerHTML : '';
              })
              .catch(function () {
                return '';
              });
          })
        ).then(function (markup) {
          var html = markup.join('');
          if (!html.trim()) return element.showEmpty();
          element.rows.innerHTML = html;
          element.empty.hidden = true;
        });
      }

      // The copy is rendered by the section and only revealed here, so it stays editable in Liquid.
      showEmpty() {
        this.rows.innerHTML = '';
        this.empty.hidden = false;
      }
    }
  );
})();

/**
 * Predictive search: a "no results" reply leaves the drawer empty, so slot the placeholder back in
 * underneath the message. CSS on .has-no-results trims the spacing and hides the product carousel.
 */
(() => {
  document.addEventListener('DOMContentLoaded', () => {
    const predictiveSearch = document.querySelector('predictive-search#predictive-search');
    const results = predictiveSearch?.querySelector('[slot="results"]');
    if (!predictiveSearch?.shadowRoot || !results) return;

    new MutationObserver(() => {
      const root = predictiveSearch.shadowRoot;
      // Only act once theme.js has switched to the results slot, and only while it is showing nothing found.
      if (!root.querySelector('slot[name="results"]')) return;

      const noResults = results.querySelector('.predictive-search__no-results') != null;
      const placeholderShown = root.querySelector('slot[name="placeholder"]') != null;

      predictiveSearch.classList.toggle('has-no-results', noResults);

      if (noResults && !placeholderShown) {
        root.innerHTML = '<slot name="results"></slot><slot name="placeholder"></slot>';
      } else if (!noResults && placeholderShown) {
        root.innerHTML = '<slot name="results"></slot>';
      }
    }).observe(results, { childList: true, subtree: true });
  });
})();

/* Reviews badge deep link (BugHerd #551)
 * Off-product-page rating badges link to {product url}#yotpo-reviews-main-widget,
 * but Yotpo builds that element client-side after load, so the browser's native
 * fragment scroll finds nothing. Keep trying until the widget exists, then scroll.
 */
(() => {
  if (window.location.hash !== '#yotpo-reviews-main-widget') return;
  let tries = 0;
  const timer = setInterval(() => {
    const el = document.getElementById('yotpo-reviews-main-widget');
    tries += 1;
    if (el) {
      clearInterval(timer);
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (tries > 100) {
      clearInterval(timer); // ~20s: Yotpo never rendered (blocked script); give up quietly
    }
  }, 200);
})();

/* Slideshow video autoplay watchdog (about-page hero, reported Aug 25).
 * Instrumented findings, in order of discovery:
 * - theme.js's restart-seek race can wedge video-media with a stale
 *   `playing` attribute while the <video> is actually paused, after which
 *   BaseMedia.play() no-ops forever.
 * - The hero <video> can sit display:none for 14-90+s on cold loads (the
 *   entrance is media-gated), so fixed recovery windows expire too early.
 * - Decisively: the hero's slideshow SUBTREE IS REPLACED ~2s after load
 *   (observed via IntersectionObserver losing the element and the video
 *   pausing at the same instant - detaching a video pauses it). Any
 *   watchdog holding element references from init keeps polling a detached
 *   tree where offsetParent is always null, while the live replacement sits
 *   frozen with a cloned stale `playing` attribute.
 * So: hold NO references. Every tick queries the document fresh and plays
 * any visible, paused, selected video slide (clearing the stale attribute
 * so video-media re-syncs from the native `play` event). A per-video pause
 * listener (WeakSet-deduped, re-armed for replacement subtrees as they are
 * discovered) gives instant recovery between ticks. Slideshows with a
 * user-facing play/pause control are left alone entirely; non-autoplay
 * cells declare data-slide-type="image" and never match.
 */
(() => {
  const seen = new WeakSet();
  const replay = (video) => {
    const vm = video.closest('video-media');
    if (vm) vm.removeAttribute('playing');
    video.muted = true;
    const p = video.play();
    if (p && p.catch) p.catch(() => {});
  };
  const eligible = (video) => {
    if (!video.isConnected || video.offsetParent === null || !video.paused) return false;
    if (video.ended && !video.loop) return false;
    if (!video.closest('.slideshow__slide')?.classList.contains('is-selected')) return false;
    if (video.closest('slideshow-carousel')?.querySelector('carousel-player-button')) return false;
    return true;
  };
  const tick = () => {
    document.querySelectorAll('.shopify-section--slideshow [data-slide-type="video"] video').forEach((video) => {
      if (!seen.has(video)) {
        seen.add(video);
        video.addEventListener('pause', () => {
          setTimeout(() => { if (eligible(video)) replay(video); }, 150);
        });
      }
      if (eligible(video)) replay(video);
    });
  };
  let ticks = 0;
  const start = () => {
    tick();
    const timer = setInterval(() => {
      ticks += 1;
      tick();
      if (ticks >= 120) clearInterval(timer);
    }, 1000);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* BH #593 - mobile: the buy area sits below the fold at load, so the theme's
   IntersectionObserver flags it "not visible" immediately and the sticky bar
   shows before the shopper ever scrolls. Hold the bar behind .pt-prescroll
   until the first real scroll; the observer keeps owning show/hide after. */
(function () {
  if (!window.matchMedia('(max-width: 699px)').matches) return;
  var bar = document.querySelector('product-sticky-add-to-cart');
  if (!bar) return;
  bar.classList.add('pt-prescroll');
  var release = function () {
    if ((window.scrollY || window.pageYOffset || 0) > 60) {
      bar.classList.remove('pt-prescroll');
      window.removeEventListener('scroll', release);
    }
  };
  window.addEventListener('scroll', release, { passive: true });
  release();
})();

/* BH #548 - on bundle PDPs whose components need choosing, the sticky bar's "+"
   renders as a plain button (.pt-scroll-to-selection) instead of a form
   submit: quick-adding silently shipped the DEFAULT component shades. Take the
   shopper to the pickers instead. */
document.addEventListener('click', function (e) {
  var btn = e.target.closest('.pt-scroll-to-selection');
  if (!btn) return;
  e.preventDefault();
  var target =
    document.querySelector('.shopify-section--main-product [class*="bundle-component"]') ||
    document.querySelector('.shopify-section--main-product .variant-picker__option') ||
    document.querySelector('.shopify-section--main-product product-form');
  if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
});

/* Image-box background matcher (eye-sculpt-set report, Aug 28). The old approach
   multiplied product photos into their grey boxes (mix-blend-mode) which washed
   out colors and still mismatched when a photo's own background differed from the
   box (#eeeeee box vs #e0e0df photo). Those blends are removed; instead, each
   box is painted the photo's OWN background color, sampled from its corners.
   Corners that disagree (busy/transparent imagery) leave the default box alone. */
(function () {
  if (window.__ptBgMatchInit) return;
  window.__ptBgMatchInit = true;

  var MAPPINGS = [
    { img: '.product-gallery__media img', box: '.product-gallery__media' },
    { img: '.product-gallery__thumbnail-list button img', box: 'button' },
    { img: '.mega-menu__linklist .image-wrap img', box: '.image-wrap' },
    { img: '.horizontal-product-card__figure img', box: '.horizontal-product-card__figure' },
    /* Primary only: the hover image (--secondary, usually a model shot) shares the
       figure, and whichever finished loading last was painting the box - dark
       green bars in the mega menu's Essentials tab (Sep 1). */
    { img: '.product-card__figure img.product-card__image--primary', box: '.product-card__figure' },
    { img: '.line-item__media img', box: '.line-item__media' },
    { img: 'img.swym-storefront-layout-grid-item-image', box: null },
    { img: '.perfect-bundle__product-img', box: '.perfect-bundle__image-wrap' }
  ];

  var colorCache = {};
  var pending = {};

  /* Sample the DISPLAYED rendition (already in the browser cache - no extra
     download) and crop just the corner regions: tiny renditions re-encode the
     jpeg and shift background colors, which painted boxes a visibly different
     grey than the photo beside them. All four corners agreeing wins; failing
     that, the two TOP corners agreeing is enough (product usually bleeds into
     the bottom, background rarely into the top). */
  function sampleColor(src, cb) {
    if (colorCache[src] !== undefined) {
      if (colorCache[src]) cb(colorCache[src]);
      return;
    }
    if (pending[src]) { pending[src].push(cb); return; }
    pending[src] = [cb];
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      var col = null;
      try {
        var w = probe.naturalWidth, h = probe.naturalHeight;
        var c = document.createElement('canvas');
        c.width = 2; c.height = 2;
        var x = c.getContext('2d', { willReadFrequently: true });
        var corners = [[0, 0], [w - 2, 0], [0, h - 2], [w - 2, h - 2]];
        var px = corners.map(function (p) {
          x.clearRect(0, 0, 2, 2);
          x.drawImage(probe, p[0], p[1], 2, 2, 0, 0, 2, 2);
          return x.getImageData(0, 0, 1, 1).data;
        });
        var agree = function (a, b) {
          return a[3] > 249 && b[3] > 249 &&
            Math.abs(a[0] - b[0]) <= 20 && Math.abs(a[1] - b[1]) <= 20 && Math.abs(a[2] - b[2]) <= 20;
        };
        var mix = function (list) {
          var r = 0, g = 0, b = 0;
          list.forEach(function (d) { r += d[0]; g += d[1]; b += d[2]; });
          return 'rgb(' + Math.round(r / list.length) + ',' + Math.round(g / list.length) + ',' + Math.round(b / list.length) + ')';
        };
        if (agree(px[0], px[1]) && agree(px[0], px[2]) && agree(px[0], px[3])) col = mix(px);
        else if (agree(px[0], px[1])) col = mix([px[0], px[1]]);
      } catch (e) { /* tainted canvas or decode issue - leave default */ }
      colorCache[src] = col;
      var cbs = pending[src] || [];
      delete pending[src];
      if (col) cbs.forEach(function (fn) { fn(col); });
    };
    probe.onerror = function () {
      colorCache[src] = null;
      delete pending[src];
    };
    probe.src = src;
  }

  function applyTo(img, boxSel) {
    var src = img.currentSrc || img.src;
    if (!src || src.indexOf('data:') === 0) return;
    var box = boxSel ? img.closest(boxSel) : img.parentElement;
    if (!box || box.dataset.ptBgFor === src) return;
    box.dataset.ptBgFor = src;
    sampleColor(src, function (col) {
      box.style.backgroundColor = col;
    });
  }

  function run(root) {
    MAPPINGS.forEach(function (m) {
      (root.querySelectorAll ? root.querySelectorAll(m.img) : []).forEach(function (img) {
        if (img.complete && img.naturalWidth) applyTo(img, m.box);
        else img.addEventListener('load', function () { applyTo(img, m.box); }, { once: true });
      });
    });
  }

  var queued = false;
  function queueRun() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      run(document);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', queueRun);
  else queueRun();
  document.addEventListener('variant:change', queueRun);
  new MutationObserver(queueRun).observe(document.documentElement, { childList: true, subtree: true });
})();

/* ------------------------------------------------------------------------------------------------
 * BUNDLE VARIANT SYNC HEALER
 * ------------------------------------------------------------------------------------------------
 * Ensures bundle product swatches (including inside Quick-Buy modals where inline <script> tags
 * from cloned <template> fragments are inert) synchronously update the form's hidden input[name="id"]
 * in 0ms BEFORE submission so that clicks on "Add to Cart" always add the selected variant.
 * ---------------------------------------------------------------------------------------------- */
(function () {
  function getFormFromScope(scope, target) {
    if (target && target.tagName === 'FORM' && target.action && target.action.indexOf('/cart/add') !== -1) {
      return target;
    }
    if (target && target.form && target.form.action && target.form.action.indexOf('/cart/add') !== -1) {
      return target.form;
    }
    if (target && target.closest) {
      var f = target.closest('form[action*="/cart/add"]');
      if (f) return f;
    }
    if (scope && scope.querySelector) {
      var f = scope.querySelector('form[action*="/cart/add"]');
      if (f) return f;
    }
    return document.querySelector('form[action*="/cart/add"]');
  }

  function findMatchedParentVariant(parentVariants, parentOptionNames, componentSelections, anySelectedValue, cards) {
    if (!parentVariants || !parentVariants.length) return null;

    // 1. Extract the active selected swatch value for each component card
    var activeCardSelections = [];
    if (cards && cards.length > 0) {
      Array.prototype.forEach.call(cards, function (card) {
        var activeGroupInput = card.querySelector('.custom-groupVariant.active input[data-option-position]:checked');
        var checkedInput = activeGroupInput || card.querySelector('input[data-option-position]:checked');
        if (!checkedInput) {
          checkedInput = card.querySelector('.custom-groupVariant.active input[data-option-position]') || card.querySelector('input[data-option-position]');
        }
        if (checkedInput) {
          var val = checkedInput.getAttribute('data-option');
          if (val) activeCardSelections.push(val.trim());
        }
      });
    }

    if (activeCardSelections.length === 0 && anySelectedValue && anySelectedValue.length > 0) {
      activeCardSelections = anySelectedValue.slice();
    }

    if (activeCardSelections.length === 0) return null;

    // Strategy 1: Iterate through activeCardSelections FIRST to find exact matching parent variant
    for (var s = 0; s < activeCardSelections.length; s++) {
      var targetVal = activeCardSelections[s].toLowerCase();
      if (!targetVal) continue;

      for (var i = 0; i < parentVariants.length; i++) {
        var v = parentVariants[i];
        var vTitle = (v.title || '').trim().toLowerCase();
        var vPublicTitle = (v.public_title || '').trim().toLowerCase();
        var vOpt1 = (v.option1 || (v.options && v.options[0]) || '').trim().toLowerCase();

        if (vTitle === targetVal || vPublicTitle === targetVal || vOpt1 === targetVal) {
          return v;
        }
      }
    }

    // Strategy 2: Multi-Option Exact Match Strategy
    for (var i = 0; i < parentVariants.length; i++) {
      var v = parentVariants[i];
      var vOpts = v.options || [v.option1, v.option2, v.option3].filter(Boolean);
      if (!vOpts.length) continue;

      var matchesAll = true;
      for (var idx = 0; idx < vOpts.length; idx++) {
        var optVal = (vOpts[idx] || '').trim().toLowerCase();
        var expectedSelectedVal = (activeCardSelections[idx] || activeCardSelections[0] || '').toLowerCase();
        if (optVal !== expectedSelectedVal) {
          matchesAll = false;
          break;
        }
      }
      if (matchesAll) return v;
    }

    // Strategy 3: Substring inclusion fallback
    for (var s = 0; s < activeCardSelections.length; s++) {
      var targetVal = activeCardSelections[s].toLowerCase();
      if (!targetVal) continue;

      for (var i = 0; i < parentVariants.length; i++) {
        var v = parentVariants[i];
        var vTitle = (v.title || '').trim().toLowerCase();
        if (vTitle.indexOf(targetVal) !== -1 || targetVal.indexOf(vTitle) !== -1) {
          return v;
        }
      }
    }

    return null;
  }

  function syncBundleToForm(scope, targetEl) {
    if (!scope || scope === document) {
      if (targetEl && targetEl.closest) {
        scope = targetEl.closest('quick-buy-modal, product-card, .shopify-section, form') || document;
      } else {
        scope = document;
      }
    }

    var form = getFormFromScope(scope, targetEl);
    var cards = scope.querySelectorAll('.bundle-product-card');
    if (!cards.length && scope !== document) {
      cards = document.querySelectorAll('.bundle-product-card');
    }

    var componentSelections = [];
    var anySelectedValue = [];

    Array.prototype.forEach.call(cards, function (card) {
      var title = (card.getAttribute('data-component-title') || '').toLowerCase();
      var values = [];

      // Ensure at least one swatch input is checked in the card
      var checkedInputs = card.querySelectorAll('input[data-option-position]:checked');
      if (checkedInputs.length === 0) {
        var firstInput = card.querySelector('.custom-groupVariant.active input[data-option-position]') || card.querySelector('input[data-option-position]');
        if (firstInput) {
          firstInput.checked = true;
          checkedInputs = [firstInput];
        }
      }

      Array.prototype.forEach.call(checkedInputs, function (input) {
        var value = input.getAttribute('data-option');
        if (!value) return;
        values.push(value);
        if (anySelectedValue.indexOf(value) === -1) anySelectedValue.push(value);

        // Update option label
        var pos = input.getAttribute('data-option-position');
        Array.prototype.forEach.call(card.querySelectorAll('.bundle-option-selected-value[data-option-position="' + pos + '"]'), function (labelEl) {
          labelEl.textContent = value;
        });
      });

      // Update active group class
      Array.prototype.forEach.call(card.querySelectorAll('.custom-groupVariant'), function (group) {
        group.classList.remove('active');
      });
      Array.prototype.forEach.call(card.querySelectorAll('.custom-groupVariant input:checked'), function (input) {
        var group = input.closest('.custom-groupVariant');
        if (group) group.classList.add('active');
      });

      if (title && values.length) componentSelections.push({ title: title, values: values });
    });

    if (!anySelectedValue.length) return;

    // A) SYNCHRONOUSLY update form.id.value from matching parent variant
    var parentVariantsScript = scope.querySelector('.bundle-parent-variants-data') || document.querySelector('.bundle-parent-variants-data');
    if (parentVariantsScript && form) {
      try {
        var parentVariants = JSON.parse(parentVariantsScript.textContent || '[]');
        var parentOptionNamesAttr = parentVariantsScript.getAttribute('data-option-names');
        var parentOptionNames = parentOptionNamesAttr ? JSON.parse(parentOptionNamesAttr) : [];

        var matchedParentVariant = findMatchedParentVariant(parentVariants, parentOptionNames, componentSelections, anySelectedValue, cards);

        if (matchedParentVariant) {
          var idInputs = form.querySelectorAll('input[name="id"], select[name="id"]');
          Array.prototype.forEach.call(idInputs, function (idInput) {
            idInput.value = String(matchedParentVariant.id);
          });
        }
      } catch (e) {}
    }

    // B) Drive hidden variant-picker radios if present
    var mainPicker = scope.querySelector('variant-picker') || document.querySelector('variant-picker');
    if (mainPicker) {
      function hasVal(list, val) { return list.indexOf(val) !== -1; }
      var groups = {};
      var radiosList = mainPicker.querySelectorAll('input[data-option-position]');
      Array.prototype.forEach.call(radiosList, function (radio) {
        if (!groups[radio.name]) groups[radio.name] = [];
        groups[radio.name].push(radio);
      });

      var changed = false;
      Object.keys(groups).forEach(function (groupKey) {
        var radios = groups[groupKey];
        var radioNameLow = (radios[0].name || '').toLowerCase();

        var owner = null;
        for (var cs = 0; cs < componentSelections.length; cs++) {
          if (radioNameLow.indexOf(componentSelections[cs].title) !== -1) {
            if (!owner || componentSelections[cs].title.length > owner.title.length) {
              owner = componentSelections[cs];
            }
          }
        }
        var candidates = owner ? owner.values : anySelectedValue;
        var target = null;
        for (var r = 0; r < radios.length; r++) {
          if (hasVal(candidates, radios[r].getAttribute('data-option'))) {
            target = radios[r];
            break;
          }
        }
        if (target && !target.checked) {
          target.checked = true;
          changed = true;
        }
      });

      if (changed) {
        var anyChecked = mainPicker.querySelector('input[data-option-position]:checked');
        if (anyChecked) anyChecked.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  function handleBundleInputChange(target) {
    if (!target) return;
    var card = target.closest && target.closest('.bundle-product-card');
    if (!card) return;

    var scope = (target.closest && target.closest('quick-buy-modal, .shopify-section, form')) || document;

    // 1. Update component option value labels & active titles
    var allOptionInputs = card.querySelectorAll('input[data-option-position]:checked');
    Array.prototype.forEach.call(allOptionInputs, function (input) {
      var pos = input.getAttribute('data-option-position');
      var val = input.getAttribute('data-option');
      Array.prototype.forEach.call(card.querySelectorAll('.bundle-option-selected-value[data-option-position="' + pos + '"]'), function (labelEl) {
        labelEl.textContent = val;
      });
    });

    Array.prototype.forEach.call(card.querySelectorAll('.custom-groupVariant'), function (group) {
      group.classList.remove('active');
    });
    Array.prototype.forEach.call(card.querySelectorAll('.custom-groupVariant input:checked'), function (input) {
      var group = input.closest('.custom-groupVariant');
      if (group) group.classList.add('active');
    });

    // 2. Component image swap if data script exists
    var script = card.querySelector('.bundle-product-variants-data');
    if (script) {
      try {
        var variants = JSON.parse(script.textContent);
        var selectedOptions = Array.prototype.map.call(
          Array.prototype.slice.call(allOptionInputs).sort(function (a, b) {
            return parseInt(a.getAttribute('data-option-position') || '0', 10) - parseInt(b.getAttribute('data-option-position') || '0', 10);
          }),
          function (input) { return input.getAttribute('data-option'); }
        );
        var matchedVariant = null;
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var match = true;
          for (var j = 0; j < selectedOptions.length; j++) {
            if (v['option' + (j + 1)] !== selectedOptions[j]) { match = false; break; }
          }
          if (match) { matchedVariant = v; break; }
        }
        if (matchedVariant && matchedVariant.featured_image && matchedVariant.featured_image.src) {
          var img = card.querySelector('.bundle-component-image');
          if (img) {
            img.src = matchedVariant.featured_image.src.replace(/(\.\w+)(\?.*)?$/, '_144x144$1$2');
            img.alt = matchedVariant.featured_image.alt || '';
          }
        }
      } catch (e) {}
    }

    // 3. Synchronously sync variant to main form & variant picker
    syncBundleToForm(scope, target);
  }

  // Document-level change listener (Capture Phase)
  document.addEventListener('change', function (e) {
    var target = e.target;
    if (target && (target.name && target.name.indexOf('-bundle-') !== -1)) {
      handleBundleInputChange(target);
    }
  }, true);

  // Document-level click listener (Capture Phase)
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target) return;

    // Swatch click
    var label = target.closest && target.closest('.bundle-product-card label, .bundle-product-card input');
    if (label) {
      var input = label.control || (label.previousElementSibling && label.previousElementSibling.matches && label.previousElementSibling.matches('input') ? label.previousElementSibling : (label.tagName === 'INPUT' ? label : null));
      if (input) {
        setTimeout(function () { handleBundleInputChange(input); }, 0);
      }
    }

    // Add To Cart button click — CRITICAL: sync synchronously BEFORE form submit
    var submitBtn = target.closest && target.closest('button[type="submit"], button[name="add"], .add_to_cart_button_wrap button, [data-action="add-to-cart"]');
    if (submitBtn) {
      var scope = (submitBtn.closest && submitBtn.closest('quick-buy-modal, product-card, .shopify-section, form')) || document;
      syncBundleToForm(scope, submitBtn);
    }
  }, true);

  // Sync on form submit (Capture Phase: ensures form.id.value is updated in 0ms BEFORE submission)
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (form && form.action && form.action.indexOf('/cart/add') !== -1) {
      var scope = (form.closest && form.closest('quick-buy-modal, product-card, .shopify-section, form')) || document;
      syncBundleToForm(scope, form);
    }
  }, true);

  // Sync on quick-buy modal before-show and after-show
  document.addEventListener('dialog:before-show', function (e) {
    var modal = e.target;
    if (modal && modal.matches && modal.matches('quick-buy-modal')) {
      syncBundleToForm(modal);
    }
  }, true);

  document.addEventListener('dialog:after-show', function (e) {
    var modal = e.target;
    if (modal && modal.matches && modal.matches('quick-buy-modal')) {
      syncBundleToForm(modal);
    }
  }, true);

  // Initial sync on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { syncBundleToForm(document); });
  } else {
    syncBundleToForm(document);
  }
})();
